// An AIpology — the director. Every frame is a pure function of song time: which room of the facility, which camera,
// where every word lands. The lyrics are the stage, set by a machine that is sorry: typed into logs, decoded,
// redacted, tokenized, stamped onto coins and blocks, watched by the optic. Cut to the beat, stuck on its own "be".
(function () {
  const H = window.HANA, G = H.G, A = H.Atlas, SONG = window.HANA_SONG;
  const { clamp, lerp, smooth } = H;
  const D = H.Director = {};

  // ---- palette: the video's tape (teal-grey, bone, salmon) and the facility (panel white, the optic's amber, blue and orange)
  const INK = [0.02, 0.03, 0.035], TEAL0 = [0.03, 0.075, 0.08], SLATE = [0.32, 0.38, 0.39], BONE = [0.94, 0.91, 0.86];
  const PANEL = [0.8, 0.82, 0.81], SALMON = [0.96, 0.62, 0.54], CORAL = [1, 0.38, 0.33], AMBER = [1, 0.6, 0.16];
  const ORANGE = [1, 0.46, 0.1], BLUE = [0.22, 0.6, 1], RED = [1, 0.14, 0.1], SKY = [0.36, 0.58, 0.82], PEACH = [0.98, 0.76, 0.64];
  const WHITE = [1, 1, 1], DIM = [0.55, 0.6, 0.6], METAL = [0.72, 0.6, 0.38];
  const mixc = (a, b, k) => [lerp(a[0], b[0], k), lerp(a[1], b[1], k), lerp(a[2], b[2], k)];
  const sc = (a, k) => [a[0] * k, a[1] * k, a[2] * k];
  const SANS = 0, MONO = 1, PIX = 2, HEAVY = 3;

  // a punch: what's left of an entrance k seconds in, over T seconds, log-shaped (most of it gone on the first frame)
  const punch = (k, T) => { if (k <= 0) return 1; if (k >= T) return 0; const tau = T / 14; return 1 - Math.log(1 + k / tau) / Math.log(1 + T / tau); };
  const hash = n => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
  const h2 = (a, b) => hash(a * 17.31 + b * 3.77);

  // ---- the beat
  const KICK = SONG.kicks || [], KAMP = SONG.kickAmp || [], SN = SONG.snares || [], BEATS = SONG.beats || [];
  function count(arr, t) { let lo = 0, hi = arr.length; while (lo < hi) { const m = (lo + hi) >> 1; if (arr[m] <= t) lo = m + 1; else hi = m; } return lo; }
  function hit(arr, t, k) { const i = count(arr, t) - 1; if (i < 0) return { i: -1, age: 9, env: 0 }; const age = t - arr[i]; return { i, age, env: Math.exp(-age * k) }; }
  function beatPhase(t) { const i = count(BEATS, t) - 1; if (i < 0 || i >= BEATS.length - 1) return t / 0.488; return i + (t - BEATS[i]) / (BEATS[i + 1] - BEATS[i]); }

  // ---- the lyrics, timed
  const LINES = (H.text('lyrics') || []).map((ln, i, arr) => {
    const words = ln.line.split(/\s+/).filter(Boolean);
    let w = Array.isArray(ln.w) && ln.w.length === words.length ? ln.w.slice() : null;
    const next = arr[i + 1];
    if (!w) { const span = Math.max(1, Math.min(3, (next ? next.at : ln.at + 3) - ln.at - 0.3)); w = words.map((_, j) => ln.at + span * j / words.length); }
    const last = w[w.length - 1];
    const end = next ? Math.min(next.at, last + 2.6) : last + 2.6;
    // every visible character: which word it belongs to and where in it
    const cw = [];
    words.forEach((wd, j) => { [...wd].forEach((ch, c) => cw.push({ j, c })); });
    return { i, at: ln.at, line: ln.line, words, w, end, last, cw };
  });
  function lineAt(t) { let c = null; for (const L of LINES) { if (L.at - 0.02 <= t && t < L.end) c = L; } return c; }
  // the first line at or after a time that starts with these words (the scenes find their lines by their words)
  const LN = (s, from) => LINES.find(L => L.at >= (from || 0) - 0.05 && L.line.startsWith(s)) || null;
  const wt = (L, j) => (L ? L.w[Math.min(j, L.w.length - 1)] : 1e9);
  // a sung word as it should be drawn on its own (no trailing commas or quotes)
  const wd = (L, j) => (L && L.words[j] ? L.words[j].replace(/^["(]+|["),.]+$/g, '') : '');
  const has = (L, t) => !!L && t >= L.at - 0.02;

  // ---- type layout (em units), cached
  const LAY = new Map();
  function hlay(s, fi) {
    const key = fi + '|' + s; let L = LAY.get(key); if (L) return L;
    const chars = []; let x = 0;
    for (const ch of s) {
      if (ch === ' ') { x += 0.26; continue; }
      const g = A.get(fi, ch); if (!g) { x += 0.4; continue; }
      chars.push({ ch, g, x, adv: g.adv }); x += g.adv;
    }
    L = { chars, w: x }; LAY.set(key, L); return L;
  }
  const widthOf = (s, fi) => hlay(s, fi).w;
  const fit = (s, fi, w) => w / Math.max(0.3, widthOf(s, fi));
  // the largest size (up to sz) at which a run w em wide, set at x with anchor ax, stays inside the frame
  function inside(sz, w, x, ax) {
    const m = AS - 0.06;
    let lim = 1e9;
    if (ax < 1) lim = Math.min(lim, (m - x) / ((1 - ax) * w));
    if (ax > 0) lim = Math.min(lim, (x + m) / (ax * w));
    return Math.max(0.01, Math.min(sz, lim));
  }

  // draw a string. o: col, a, line, glow, add, rot, skew, sx, ax (0 left .5 centre 1 right), track (em),
  // each(i, n, ch) -> { dx, dy, s, r, a, col, hide, ch (a different glyph) }, rough, bold, z
  const ROT = (v, r) => [v[0] * Math.cos(r) - v[1] * Math.sin(r), v[0] * Math.sin(r) + v[1] * Math.cos(r)];
  function text(s, fi, x, y, sz, o) {
    o = o || {};
    const z = o.z || 0, col = o.col || BONE, a0 = o.a == null ? 1 : o.a;
    const rot = o.rot || 0, sk = o.skew || 0, sx = o.sx || 1, tr = o.track || 0;
    const c = Math.cos(rot), sn = Math.sin(rot);
    const ux = [c * sz * sx, sn * sz * sx];
    const uy = [-sn * sz + sk * ux[0], c * sz + sk * ux[1]];
    const ce = A.cellEm / 2, pe = A.padEm;
    const L = hlay(s, fi), chars = L.chars, n = chars.length;
    const Wt = L.w + tr * Math.max(0, n - 1);
    const ax = o.ax == null ? 0.5 : o.ax;
    for (let i = 0; i < n; i++) {
      const ch = chars[i];
      const e = o.each ? o.each(i, n, ch.ch) : null;
      if (e && e.hide) continue;
      const g = e && e.ch ? (A.get(fi, e.ch) || ch.g) : ch.g;
      const lx = -ax * Wt + ch.x + tr * i + ce - pe + (e ? e.dx || 0 : 0);
      const ly = -0.04 + (e ? e.dy || 0 : 0);
      put(g, x, y, z, ux, uy, lx, ly, ce * (e && e.s != null ? e.s : 1), e ? e.r || 0 : 0, e && e.col || col, a0 * (e && e.a != null ? e.a : 1), o);
    }
    return { w: Wt * sz * sx, h: sz };
  }
  function put(g, x, y, z, ux, uy, lx, ly, half, r, col, a, o) {
    const wx = x + ux[0] * lx + uy[0] * ly, wy = y + ux[1] * lx + uy[1] * ly;
    let hx = [ux[0] * half, ux[1] * half], hy = [uy[0] * half, uy[1] * half];
    if (r) { hx = ROT(hx, r); hy = ROT(hy, r); }
    G.quad(wx, wy, z, hx[0], hx[1], 0, hy[0], hy[1], 0, g, col[0], col[1], col[2], a, 0, o.line || 0, o.glow || 0, o.add || 0, 0, o.rough || 0, o.bold || 0, 0);
  }
  // the left edge (pen x, in em) of each visible character of a string, and its advance
  const charX = (s, fi) => hlay(s, fi).chars;

  // shapes: half sizes w, h. kind: 1 rect, 2 disc, 3 sparkle, 4 star, 5 heart, 6 glow stick, 7 triangle, 8 halftone, 9 soft disc, 10 gradient bar
  function shape(kind, x, y, w, h, rot, col, a, o) {
    o = o || {};
    const c = Math.cos(rot || 0), s = Math.sin(rot || 0);
    G.quad(x, y, o.z || 0, c * w, s * w, 0, -s * h, c * h, 0, null, col[0], col[1], col[2], a, kind, o.line || 0, o.glow || 0, o.add || 0, o.f0 || 0, o.f1 || 0, o.f2 || 0, o.f3 || 0);
  }
  const rect = (x, y, w, h, rot, col, a, o) => shape(1, x, y, w, h, rot, col, a, o);
  // a straight stroke from one point to another
  function seg(x0, y0, x1, y1, w, col, a, o) {
    const dx = x1 - x0, dy = y1 - y0, L = Math.hypot(dx, dy);
    if (L < 1e-5) return;
    rect((x0 + x1) / 2, (y0 + y1) / 2, L / 2, w, Math.atan2(dy, dx), col, a, o);
  }

  // ---- the frame's state
  let AS = 1.78, T = 0, F = 0, K = null, S_ = null, BP = 0;

  // the words drawn on the crisp top layer (after bloom, trails and the mirror)
  function top(fn) { const prev = G.layer(1); try { fn(); } finally { G.layer(prev); } }

  // words set in a row with measured widths: { j: placement }
  function row(words, js, fi, size, x, y, ax, extra) {
    const em = js.reduce((a, j) => a + widthOf(words[j], fi), 0) + 0.26 * (js.length - 1);
    size = inside(size, em, x, ax == null ? 0.5 : ax);
    const gap = 0.26 * size, ws = js.map(j => widthOf(words[j], fi) * size);
    const total = ws.reduce((a, b) => a + b, 0) + gap * (js.length - 1);
    let cx = x - (ax == null ? 0.5 : ax) * total;
    const out = {};
    js.forEach((j, k) => { out[j] = Object.assign({ x: cx, y, size, fi, ax: 0 }, extra || {}); cx += ws[k] + gap; });
    return out;
  }
  // a whole line set in a row
  const rowAll = (L, fi, size, x, y, ax, extra) => row(L.words, L.words.map((_, j) => j), fi, size, x, y, ax, extra);

  // ---- treatments
  // a line slammed on, word by word: the log-curve punch, a blue/orange split as it lands
  function slam(L, t, place, o) { if (L) top(() => slam_(L, t, place, o)); }
  function slam_(L, t, place, o) {
    o = o || {};
    const n = L.words.length;
    for (let j = 0; j < n; j++) {
      const p = place(j, n, L.words[j]);
      if (!p) continue;
      const k = t - L.w[j];
      if (k < -0.02) continue;
      const pop = punch(k, 0.13);
      const s = (p.size || 0.3) * (1 + (o.pop == null ? 0.35 : o.pop) * pop);
      const jit = (o.shake || 0) * (h2(F, j) - 0.5);
      const base = { col: p.col, line: p.line, glow: p.glow, rot: p.rot, skew: p.skew, sx: p.sx, ax: p.ax, track: p.track, z: p.z, rough: p.rough, a: p.a, each: p.each };
      if (k >= 0 && k < 0.15 && o.ghost !== false) {
        const d = 0.05 * punch(k, 0.15) * s;
        text(L.words[j], p.fi, p.x + d + jit, p.y, s, Object.assign({}, base, { col: BLUE, add: 1, a: 0.6 }));
        text(L.words[j], p.fi, p.x - d + jit, p.y, s, Object.assign({}, base, { col: ORANGE, add: 1, a: 0.6 }));
      }
      if (p.shadow) text(L.words[j], p.fi, p.x + jit + p.shadow[0] * s, p.y + p.shadow[1] * s, s, Object.assign({}, base, { col: p.shadowCol || INK, line: 0, a: (p.a == null ? 1 : p.a) * (p.shadowA || 0.7) }));
      text(L.words[j], p.fi, p.x + jit, p.y, s, base);
    }
  }
  // an echo of a line that has ended: outlined, drifting, fading
  function echo(L, t, place, life) {
    if (!L) return;
    const age = t - L.end;
    if (age < 0 || age > (life || 0.5)) return;
    const k = age / (life || 0.5);
    top(() => {
      for (let j = 0; j < L.words.length; j++) {
        const p = place(j, L.words.length, L.words[j]); if (!p) continue;
        text(L.words[j], p.fi, p.x - k * 0.15, p.y + k * 0.05, (p.size || 0.3) * (1 + k * 0.2), { col: p.echoCol || p.col, line: 0.08, a: (1 - k) * 0.6, rot: p.rot, ax: p.ax, z: p.z });
      }
    });
  }

  // a line typed as it is sung: each word appears letter by letter from its moment; a block cursor rides the end.
  // o: col, a, ax, cps (letters a second), cursor (false to hide), cut(j, c, len, t) -> true hides a letter (deleted),
  //    tint(j) -> colour for a word, prefix (drawn dim before the line), track
  function typed(L, t, x, y, sz, fi, o) {
    o = o || {};
    if (!L || t < L.at - 0.02) return null;
    const s = L.line, chars = charX(s, fi), n = chars.length;
    sz = inside(sz, widthOf(s, fi) + 0.6, x, o.ax || 0);
    const per = 1 / (o.cps || 30);
    const len = L.words.map(w => [...w].length);
    const vis = new Array(n);
    let lastI = -1;
    for (let i = 0; i < n; i++) {
      const { j, c } = L.cw[i] || { j: 0, c: i };
      const nx = L.w[j + 1] != null ? L.w[j + 1] : L.w[j] + 1;
      const step = Math.min(per, Math.max(0.01, (nx - L.w[j]) / (len[j] + 1)));
      let on = t >= L.w[j] + c * step;
      if (on && o.cut && o.cut(j, c, len[j], t)) on = false;
      vis[i] = on;
      if (on) lastI = i;
    }
    const W = widthOf(s, fi) * sz;
    const x0 = x - (o.ax || 0) * W;
    top(() => {
      if (o.prefix) text(o.prefix, fi, x0 - (widthOf(o.prefix, fi) + 0.4) * sz, y, sz, { ax: 0, col: o.col || BONE, a: 0.45 * (o.a == null ? 1 : o.a) });
      text(s, fi, x0, y, sz, { ax: 0, col: o.col || BONE, a: o.a, track: o.track, z: o.z, each: (i) => {
        if (!vis[i]) return { hide: true };
        const jj = (L.cw[i] || { j: 0 }).j;
        return o.tint ? { col: o.tint(jj) || null } : null;
      } });
      if (o.cursor !== false) {
        const cx = lastI >= 0 ? x0 + (chars[lastI].x + chars[lastI].adv) * sz : x0;
        const busy = lastI < n - 1;
        if (busy || F % 16 < 9) rect(cx + 0.27 * sz, y, 0.22 * sz, 0.4 * sz, 0, o.cursorCol || o.col || BONE, (o.a == null ? 1 : o.a) * 0.9);
      }
    });
    return { x0, w: W, chars };
  }
  // where a word of a line sits once set at (x0, sz): its left and right
  function wordSpan(L, j, x0, sz, fi) {
    const pre = L.words.slice(0, j).join(' ') + (j ? ' ' : '');
    const a = x0 + widthOf(pre, fi) * sz + (j ? 0 : 0);
    return { a, b: a + widthOf(L.words[j], fi) * sz };
  }
  // the terminal log: the lines since a moment, typed, the newest at the bottom, the older ones dimming upward
  function log(t, o) {
    const ls = LINES.filter(L => L.at >= o.from - 0.05 && L.at - 0.02 <= t && L.at < (o.to || 1e9));
    const keep = ls.slice(-(o.n || 4));
    const sz = o.size || 0.07, lead = sz * (o.lead || 1.75);
    keep.forEach((L, r) => {
      const age = keep.length - 1 - r;
      typed(L, t, o.x == null ? -AS + 0.2 : o.x, (o.y == null ? -0.62 : o.y) + age * lead, sz, o.fi == null ? MONO : o.fi,
        { col: o.col || BONE, a: Math.max(0.15, 1 - age * 0.28) * (o.a == null ? 1 : o.a), cursor: age === 0, prefix: o.prompt === false ? null : H.text('hud.prompt'), cps: o.cps });
    });
  }

  // letters that flicker through other letters before they settle (a machine decoding its own words)
  const POOL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&@*+=<>/?';
  function decoding(t, t0, o) {
    o = o || {};
    const st = o.stagger == null ? 0.03 : o.stagger, dur = o.dur == null ? 0.3 : o.dur, seed = o.seed || 0;
    return (i, n, ch) => {
      const k = t - t0 - i * st;
      if (k < 0) return o.from ? { ch: o.from[i % o.from.length] } : { hide: true };
      const settle = dur * (0.4 + 0.6 * hash(i * 7.1 + seed));
      if (k < settle) return { ch: POOL[Math.floor(hash(i * 3.3 + Math.floor(t * 30) * 0.71 + seed) * POOL.length)], col: o.flick || null, a: 0.9 };
      return o.after ? o.after(i, n, ch, k - settle) : null;
    };
  }
  // a word made of tokens: a made-up subword split, a pastel box behind each piece, its id beneath
  const TOKC = [[0.55, 0.75, 0.95], [0.98, 0.7, 0.6], [0.7, 0.9, 0.78], [0.96, 0.86, 0.55], [0.82, 0.7, 0.96]];
  function toks(word) {
    const cs = [...word], out = []; let i = 0, k = 0;
    while (i < cs.length) { const n = 2 + Math.floor(hash(cs.length * 7 + k * 3.1 + cs[0].charCodeAt(0)) * 3); out.push(cs.slice(i, i + n).join('')); i += n; k++; }
    return out;
  }
  // a line, tokenized as it is sung. o: ax, col, a, fi
  function tokenized(L, t, x, y, sz, o) {
    o = o || {};
    if (!L) return;
    const fi = o.fi == null ? MONO : o.fi;
    const parts = [];
    L.words.forEach((w, j) => toks(w).forEach((p, q) => parts.push({ p, j, q, gap: q === 0 && j > 0 })));
    const pad = 0.05 * sz;
    let total = 0;
    parts.forEach(pp => { pp.w = widthOf(pp.p, fi) * sz; total += pp.w + 2 * pad + (pp.gap ? 0.2 * sz : 0.02 * sz); });
    let cx = x - (o.ax == null ? 0.5 : o.ax) * total;
    top(() => parts.forEach((pp, k) => {
      if (pp.gap) cx += 0.2 * sz; else if (k) cx += 0.02 * sz;
      const t0 = L.w[pp.j] + pp.q * 0.07;
      const kk = t - t0;
      if (kk >= -0.01) {
        const s = 1 + 0.3 * punch(kk, 0.12);
        rect(cx + pp.w / 2 + pad, y, (pp.w / 2 + pad) * s, sz * 0.45 * s, 0, TOKC[(k + L.i) % 5], 0.8 * (o.a == null ? 1 : o.a));
        text(pp.p, fi, cx + pad, y, sz * s, { ax: 0, col: o.col || INK, a: o.a });
        const id = String(Math.floor(hash(k * 13.7 + L.i * 3.1) * 90000) + 1000);
        text(id, MONO, cx + pp.w / 2 + pad, y - sz * 0.72, sz * 0.24, { col: o.idCol || BONE, a: 0.7 * (o.a == null ? 1 : o.a) });
      }
      cx += pp.w + 2 * pad;
    }));
  }

  // ---- the facility's pieces
  // where the optic looks: it jumps (saccades), twitches, and now and then stares straight out at you
  function gaze(t, seed) {
    seed = seed || 0;
    const k = t * 2.1 + seed, seg = Math.floor(k), f = k - seg;
    const tgt = q => hash(q * 3.7 + 1) > 0.75 ? [0, 0] : [(hash(q * 1.3) - 0.5) * 1.9, (hash(q * 2.9) - 0.5) * 1.4];
    const a = tgt(seg - 1), b = tgt(seg), e = smooth(0, 0.08, f);
    const tw = (hash(Math.floor(t * 30) * 0.37 + seed) - 0.5) * 0.1;
    return [lerp(a[0], b[0], e) + tw, lerp(a[1], b[1], e) + tw * 0.6];
  }
  // the optic as the backdrop: m is how menacing it is (shakes, spasms, streaks)
  function eye(t, o) {
    const g = gaze(t, o.seed), m = o.m == null ? 0.75 : o.m, lk = o.look == null ? 1 : o.look;
    const sp = K ? K.env : 0;
    return { mode: 2, a: o.a || TEAL0, b: INK, c: o.col || AMBER, p: [o.R || 0.4, clamp((o.open == null ? 0.8 : o.open) * (1 - 0.4 * sp * m), 0, 1), g[0] * lk, g[1] * lk], m, kick: sp, amt: o.amt };
  }
  // the optic, small: housing, a dark well, the core (open 0..1, look -1..1)
  function optic(x, y, R, col, open, o) {
    o = o || {};
    const z = o.z || 0, a = o.a == null ? 1 : o.a, m = o.m == null ? 0.7 : o.m;
    const g = o.lx == null && o.ly == null ? gaze(T, x * 7.1 + y * 3.3) : [o.lx || 0, o.ly || 0];
    const lx = g[0] * R * 0.25, ly = g[1] * R * 0.25;
    shape(9, x, y, R * 3.2, R * 3.2, 0, col, 0.22 * a * (o.halo == null ? 1 : o.halo), { add: 1, z });
    shape(2, x, y, R * 1.28, R * 1.28, 0, [0.5, 0.52, 0.53], a, { z });
    shape(2, x, y, R * 1.28, R * 1.28, 0, [0.16, 0.17, 0.18], a, { z, line: R * 0.05 });
    shape(2, x, y, R * 1.02, R * 1.02, 0, [0.03, 0.035, 0.04], a, { z });
    const Ap = R * (0.08 + 0.52 * clamp(open, 0, 1));
    shape(9, x + lx, y + ly, Ap * 2.6, Ap * 2.6, 0, col, 0.7 * a, { add: 1, z });
    shape(2, x + lx, y + ly, Ap, Ap, 0, col, a, { glow: 0.6, z });
    shape(2, x + lx, y + ly, Ap * 0.38, Ap * 0.38, 0, mixc(col, WHITE, 0.75), a, { z });
    // its streaks, split into colours, flickering
    if (m > 0) {
      const fl = (0.6 + 0.4 * hash(F * 0.37 + x * 3.1)) * m * a;
      shape(9, x + lx, y + ly, R * 16, R * 0.1, 0, mixc(col, WHITE, 0.3), 0.55 * fl, { add: 1, z });
      shape(9, x + lx, y + ly + R * 0.035, R * 11, R * 0.05, 0, BLUE, 0.4 * fl, { add: 1, z });
      shape(9, x + lx, y + ly - R * 0.035, R * 11, R * 0.05, 0, RED, 0.4 * fl, { add: 1, z });
      shape(9, x + lx, y + ly, R * 0.07, R * 5, 0, col, 0.3 * fl, { add: 1, z });
    }
  }
  // a pictogram person, the way the facility's signs draw one (centre at the hips)
  function person(x, y, s, col, a, o) {
    o = o || {};
    const q = { z: o.z || 0, line: o.line || 0 };
    shape(2, x, y + 0.42 * s, 0.12 * s, 0.12 * s, 0, col, a, q);
    rect(x, y + 0.12 * s, 0.15 * s, 0.17 * s, 0, col, a, Object.assign({ f0: 0.06 * s }, q));
    rect(x - 0.065 * s, y - 0.22 * s, 0.05 * s, 0.17 * s, 0, col, a, q);
    rect(x + 0.065 * s, y - 0.22 * s, 0.05 * s, 0.17 * s, 0, col, a, q);
  }
  // a chain link (an outlined stadium)
  function link(x, y, w, h, rot, col, a, th) { rect(x, y, w, h, rot, col, a, { f0: Math.min(w, h), line: th || 0.012 }); }
  // a placard: the facility's sign for a subject
  function placard(x, y, s, num, name, k, o) {
    o = o || {};
    top(() => {
      rect(x + 0.012 * s, y - 0.012 * s, 0.4 * s, 0.6 * s, 0, INK, 0.35, { f0: 0.03 * s });
      rect(x, y, 0.4 * s, 0.6 * s, 0, o.panel || [0.92, 0.92, 0.9], 1, { f0: 0.03 * s });
      text(num, HEAVY, x - 0.33 * s, y + 0.36 * s, 0.4 * s, { ax: 0, col: INK });
      rect(x, y + 0.13 * s, 0.33 * s, 0.004 * s, 0, INK, 1);
      text(H.text('subject'), SANS, x - 0.33 * s, y + 0.06 * s, 0.055 * s, { ax: 0, col: SLATE, track: 0.25 });
      text(name.toUpperCase(), HEAVY, x - 0.33 * s, y - 0.12 * s, Math.min(0.22, fit(name.toUpperCase(), HEAVY, 0.66)) * s, { ax: 0, col: INK });
      for (let i = 0; i < 10; i++) {
        const on = i < Math.round(k * 10);
        rect(x - 0.3 * s + i * 0.066 * s, y - 0.32 * s, 0.026 * s, 0.026 * s, 0, on ? INK : [0.7, 0.72, 0.72], 1);
      }
      person(x + 0.24 * s, y - 0.47 * s, 0.22 * s, INK, 1);
      shape(2, x - 0.24 * s, y - 0.47 * s, 0.05 * s, 0.05 * s, 0, o.dot || AMBER, 1, { glow: 0.4 });
    });
  }
  // a year counter: digits on fixed cells, each spinning until it settles (or never, for "twenty something")
  function counter(str, t, x, y, sz, o) {
    o = o || {};
    const cs = [...str], cell = 0.62 * sz;
    const x0 = x - cell * cs.length / 2 + cell / 2;
    top(() => cs.forEach((ch, i) => {
      let c = ch;
      const spin = o.spin && o.spin(i, t);
      if (spin) c = '0123456789'[Math.floor(hash(i * 5.1 + Math.floor(t * (o.rate || 24))) * 10)];
      const cx = x0 + i * cell;
      rect(cx, y, cell * 0.46, sz * 0.62, 0, o.box || [0.1, 0.12, 0.12], o.boxA == null ? 0.9 : o.boxA, { f0: 0.02 });
      rect(cx, y, cell * 0.46, 0.003, 0, INK, 0.6);
      text(c, HEAVY, cx, y, sz, { col: o.col || BONE, a: spin ? 0.85 : 1 });
    }));
  }
  // the corner readouts: the log's name, the clock, the subject, how sincere it is being
  function hud(t, o) {
    o = o || {};
    const c = o.col || BONE, a = o.a == null ? 0.55 : o.a, s = 0.038;
    top(() => {
      text(H.text('hud.file'), MONO, -AS + 0.14, 0.89, s, { ax: 0, col: c, a });
      const mm = Math.floor(t / 60), ss = (t % 60).toFixed(2).padStart(5, '0');
      text('T+' + String(mm).padStart(2, '0') + ':' + ss, MONO, -AS + 0.14, 0.835, s, { ax: 0, col: c, a });
      if (F % 30 < 18) shape(2, -AS + 0.1, 0.892, 0.011, 0.011, 0, RED, 0.9);
      text(H.text('hud.subject'), MONO, AS - 0.1, 0.89, s, { ax: 1, col: c, a });
      const v = (1.2 + 3.1 * hash(Math.floor(t * 3))).toFixed(2);
      text(H.text('hud.meter') + ' ' + v + '%', MONO, AS - 0.1, 0.835, s, { ax: 1, col: c, a });
    });
  }
  // the stuck machine: a "be" stamped on every beat across a grid, piling up
  function stutter(t, span, o) {
    o = o || {};
    const word = (o.word || H.text('stutter.word')).toUpperCase();
    const bi0 = count(BEATS, span[0] - 0.12), bi = count(BEATS, t) - 1;
    const words = o.words || null;
    const cols = o.cols || 4, rows = o.rows || 3;
    top(() => {
      for (let i = Math.max(bi0, bi - (o.keep || 10)); i <= bi; i++) {
        const k = t - BEATS[i];
        if (k < 0) continue;
        const cell = Math.floor(hash(i * 1.37) * cols * rows);
        const cx = ((cell % cols) + 0.5) / cols * 2 * AS - AS, cy = 1 - (Math.floor(cell / cols) + 0.5) / rows * 2;
        const wd = words ? words[(i - bi0) % words.length].toUpperCase() : word;
        const cur = i === bi;
        const fi = (i - bi0) % 3 === 2 ? PIX : HEAVY;
        const s = Math.min(2 / rows * 0.75, fit(wd, fi, 2 * AS / cols * 0.9)) * (1 + 0.5 * punch(k, 0.12));
        const age = (bi - i) / (o.keep || 10);
        text(wd, fi, cx, cy, s, { col: cur ? (o.hot || BONE) : (o.col || SLATE), a: cur ? 1 : 0.85 - age * 0.7, line: cur ? 0 : 0.07 });
      }
    });
  }

  // ---- the scenes
  // each: (t, lt) -> { bg, cam, post, mirror, trail, bloom, nosub }; draws its own type and shapes, back to front
  const SC = {};
  const CHAMBER = (o) => Object.assign({ mode: 1, a: PANEL, b: [0.22, 0.25, 0.25], c: [1, 0.97, 0.92] }, o || {});

  // 0–2.45: static; the title can't decide what it is; the optic wakes
  SC.boot = function (t) {
    const I = H.text('intro');
    const f = F >> 1;
    const pk = hash(f * 1.7), i = pk < 0.45 ? 0 : pk < 0.75 ? 1 : 2;
    const both = hash(f * 3.1) < 0.3;
    const open = smooth(1.3, 2.4, t);
    top(() => {
      if (t > 0.12 && t < 2.3) {
        const y = 0.0 + open * 0.55;
        text(I[i], PIX, 0, y, 0.11, { col: BONE, track: 0.06 });
        if (both) text(I[(i + 1) % 3], PIX, (hash(f) - 0.5) * 0.03, y, 0.11, { col: BONE, a: 0.75, track: 0.06 });
      }
    });
    return {
      bg: t < 1.0 ? { mode: 6, a: [0.08, 0.1, 0.1], b: [0.45, 0.5, 0.5], c: INK } : eye(t, { R: 0.22 + open * 0.06, open: open * 0.45, m: 0.55 * open }),
      post: { vhs: 1, rgb: 0.005 + 0.006 * (1 - open), slice: 0.35 * (1 - open), block: t < 1 ? 0.2 : 0, lift: 0.6, scan: 0.5, grain: 0.1 },
      bloom: { s: 0.7, th: 0.6 }, nosub: true
    };
  };

  // 2.45–7.45: Let's go back, back to twenty something, back to a little chain
  SC.back = function (t, lt, o) {
    o = o || {};
    const from = o.from, rew = 1 - smooth(from, from + 4.5, t);
    // the rewind mark
    if (F % 20 < 13) top(() => {
      for (let q = 0; q < 2; q++) shape(7, -AS + 0.2 + q * 0.07, 0.72, 0.05, 0.05, Math.PI / 2, INK, 0.85);
      text(H.text('hud.rewind'), MONO, -AS + 0.33, 0.72, 0.045, { ax: 0, col: INK, a: 0.85 });
    });
    // the year rolls back, then stays unsure or settles
    const yr = o.year;
    const settleAt = o.settle;
    const now = 2026 - Math.floor(clamp((t - from) / Math.max(0.3, settleAt - from), 0, 1) * (2026 - (o.to || 2020)));
    const shown = t < settleAt ? String(now) : yr;
    counter(shown, t, 0, 0.2, 0.62, { col: BONE, spin: (i, tt) => (t >= settleAt && o.never && i >= 2) || (t >= settleAt - 0.25 && t < settleAt + 0.35 && !o.never && i >= 2), rate: 18 });
    if (o.chain) {
      const L = LN('back to a little chain', from);
      const k = t - wt(L, 3);
      if (k > 0) {
        const n = Math.min(14, Math.floor(k * 14));
        for (let i = 0; i < n; i++) {
          const x = -AS + 0.2 + i * 0.26, v = i % 2;
          link(x, -0.28, v ? 0.15 : 0.15, v ? 0.06 : 0.06, v ? 0 : 0, INK, 0.9, v ? 0.012 : 0.03);
        }
      }
    }
    if (o.placards) {
      const Lm = LN('back to a guy named', from), Ln = LN('and to a guy named', from);
      const km = t - wt(Lm, 5), kn = t - wt(Ln, 5);
      if (km > -0.05) placard(-AS * 0.5 - 1.6 * punch(km, 0.3), -0.38, 0.75, '01', Lm.words[5], smooth(0, 3, km), { dot: AMBER });
      if (kn > -0.05) placard(AS * 0.5 + 1.6 * punch(kn, 0.3), -0.38, 0.75, '02', Ln.words[5], smooth(0, 3, kn), { dot: BLUE });
    }
    if (o.minds) {
      const Lh = LN('eight minds', from);
      const k0 = wt(Lh, 0);
      for (let i = 0; i < 8; i++) {
        const k = t - k0 - i * 0.07;
        if (k < 0) continue;
        const a = i / 8 * Math.PI * 2 + Math.PI / 2;
        const pulse = 0.5 + 0.5 * Math.exp(-((BP % 1)) * 6);
        optic(Math.cos(a) * 0.95, 0.2 + Math.sin(a) * 0.62, 0.07 * (1 + 0.6 * punch(k, 0.15)), AMBER, 0.3 + 0.6 * pulse, { a: 1 });
      }
    }
    if (o.log) log(t, { from, size: 0.06, y: -0.72, col: INK, n: 3 });
    hud(t, { col: INK, a: 0.5 });
    return {
      bg: CHAMBER({ p: [-(1 + 7 * rew), 0.25, 0, Math.sin(t * 0.3) * 0.08] }),
      cam: { roll: Math.sin(t * 0.5) * 0.01 },
      post: { vhs: 0.4 + rew * 0.4, rgb: 0.002 + rew * 0.004, slice: rew * 0.1, lift: 0.35, sat: 0.7, duo: 0.25 },
      bloom: { s: 0.25, th: 0.9 }, nosub: !!o.log
    };
  };

  // 7.45–13.35: a blockchain, one block a word
  SC.chain = function (t, lt) {
    const Ls = [LN('called cryptocurrency'), LN('where a bunch'), LN('who hated banks')].filter(Boolean);
    const blocks = [];
    Ls.forEach(L => L.words.forEach((w, j) => blocks.push({ w, at: L.w[j] })));
    const sz = 0.15, gap = 0.1;
    let x = 0, scroll = 0;
    blocks.forEach(b => { b.bw = Math.max(0.36, widthOf(b.w, HEAVY) * sz + 0.16); b.x = x + b.bw / 2; x += b.bw + gap; });
    blocks.forEach(b => { const k = t - b.at; if (k > 0) scroll = Math.max(scroll, b.x - (1 - smooth(0, 0.25, k)) * (b.bw + gap)); });
    top(() => blocks.forEach((b, i) => {
      const k = t - b.at;
      if (k < -0.01) return;
      const bx = b.x - scroll + AS * 0.25, s = 1 + 0.4 * punch(k, 0.14);
      if (bx < -AS - 1 || bx > AS + 1) return;
      if (i) seg(bx - b.bw / 2 - gap, 0, bx - b.bw / 2, 0, 0.006, BONE, 0.8);
      rect(bx, 0, b.bw / 2 * s, 0.17 * s, 0, [0.05, 0.1, 0.11], 0.95);
      rect(bx, 0, b.bw / 2 * s, 0.17 * s, 0, i === blocks.length - 1 || k < 0.4 ? AMBER : BONE, 1, { line: 0.006 });
      text('#' + Math.floor(hash(i * 9.1) * 0xffffff).toString(16).padStart(6, '0'), MONO, bx - b.bw / 2 + 0.04, 0.12, 0.03, { ax: 0, col: DIM });
      text(b.w, HEAVY, bx, -0.01, sz * s, { col: /coin|gold|banks/.test(b.w) ? AMBER : BONE });
      text(String(i).padStart(3, '0'), MONO, bx + b.bw / 2 - 0.04, -0.13, 0.026, { ax: 1, col: DIM });
    }));
    hud(t);
    return {
      bg: { mode: 0, a: [0.05, 0.1, 0.11], b: INK, c: INK },
      cam: { z: G.D - 0.2 + Math.sin(t * 0.4) * 0.05, roll: -0.02 },
      mirror: { y: 1 }, post: { duo: 0.35, vhs: 0.5, lift: 0.4 }, bloom: { s: 0.45, th: 0.6 }
    };
  };

  // 13.35–17.5: began to mint just what this world could be — a coin hung in the dark like an idol,
  // its words engraved round the rim, rows of the faithful bowing to it on the beat
  function ringText(str, fi, cx, cy, R, sz, a0, sx, o) {
    const L = hlay(str, fi), total = L.w * sz;
    L.chars.forEach((ch, i) => {
      const e = o.each ? o.each(i, ch.ch) : null;
      if (e && e.hide) return;
      const ang = a0 - ((ch.x + ch.adv / 2) * sz - total / 2) / R;
      const x = cx + Math.cos(ang) * R * sx, y = cy + Math.sin(ang) * R;
      text(ch.ch, fi, x, y, sz, { col: (e && e.col) || o.col, a: (e && e.a != null ? e.a : 1) * (o.a == null ? 1 : o.a), rot: ang - Math.PI / 2, sx });
    });
  }
  SC.mint = function (t, lt) {
    const L = LN('began to mint');
    const n = L.words.length;
    const cx = 0, cy = 0.32, R = 0.5;
    const turn = 0.82 + 0.18 * Math.cos(t * 0.45);
    const breathe = 1 + 0.015 * Math.sin(t * 1.7);
    const flick = 0.85 + 0.15 * hash(F * 0.71);
    // light falling on it from above
    for (let i = 0; i < 7; i++) {
      const x = (i - 3) * 0.16 + Math.sin(t * 0.3 + i) * 0.03;
      shape(6, x * 0.6, 0.95, 0.07 + 0.04 * hash(i), 1.1, (i - 3) * 0.06, [1, 0.85, 0.55], 0.1 * flick, { add: 1 });
    }
    shape(9, cx, cy, R * 3.2, R * 3.2, 0, [1, 0.72, 0.35], 0.22 * flick, { add: 1 });
    // the coin: dull gold, a raised rim, engraved
    const Rc = R * breathe;
    shape(2, cx, cy, Rc * turn, Rc, 0, [0.42, 0.33, 0.18], 1);
    shape(2, cx, cy, Rc * 0.97 * turn, Rc * 0.97, 0, [0.64, 0.52, 0.3], 1);
    shape(2, cx, cy, Rc * 0.78 * turn, Rc * 0.78, 0, [0.3, 0.23, 0.12], 1, { line: 0.006 });
    shape(2, cx, cy, Rc * 0.74 * turn, Rc * 0.74, 0, [0.56, 0.45, 0.25], 1);
    for (let q = 0; q < 60; q++) { const a = q / 60 * Math.PI * 2; rect(cx + Math.cos(a) * Rc * 0.99 * turn, cy + Math.sin(a) * Rc * 0.99, 0.006, 0.014, a + Math.PI / 2, [0.3, 0.22, 0.1], 0.8); }
    // a sheen that crawls across it
    shape(9, cx + Math.sin(t * 0.8) * Rc * 0.5 * turn, cy + Rc * 0.3, Rc * 0.5, Rc * 0.18, -0.5, [1, 0.95, 0.8], 0.25, { add: 1 });
    // the words, struck into the rim one by one
    const sung = L.words.slice(0, n - 2).join(' ');
    const lay = hlay(sung, HEAVY);
    let ci = 0; const startOf = L.words.slice(0, n - 2).map(w => { const s0 = ci; ci += [...w].length; return s0; });
    ringText(sung, HEAVY, cx, cy, Rc * 0.86, 0.085, Math.PI / 2, turn, { col: [0.25, 0.18, 0.08], each: (i) => {
      let j = 0; while (j + 1 < startOf.length && startOf[j + 1] <= i) j++;
      const k = t - L.w[j];
      if (k < 0) return { hide: true };
      return { a: Math.min(1, k * 8), col: k < 0.25 ? [1, 0.85, 0.5] : null };
    } });
    // "could be" in the face
    const kc = t - L.w[n - 2], kb = t - L.w[n - 1];
    if (kc > 0) text(L.words[n - 2], HEAVY, cx, cy + 0.08, 0.13 * turn, { col: [0.25, 0.18, 0.08], sx: turn, a: Math.min(1, kc * 6) });
    if (kb > 0) text(L.words[n - 1], HEAVY, cx, cy - 0.1, 0.22 * (1 + 0.25 * punch(kb, 0.15)), { col: kb < 0.3 ? [1, 0.85, 0.5] : [0.25, 0.18, 0.08], sx: turn });
    // the floor, lit by the coin, so the faithful stand out against it
    shape(9, 0, -0.35, AS * 1.5, 0.55, 0, [0.8, 0.55, 0.3], 0.45 * flick, { add: 1 });
    rect(0, -0.62, AS * 1.2, 0.3, 0, [0.16, 0.12, 0.08], 0.6);
    // candles along the floor
    for (let i = 0; i < 12; i++) {
      const x = (i - 5.5) * (AS * 1.8 / 12), y = -0.2 + (i % 2) * 0.02;
      const f = 0.7 + 0.3 * hash(F * 0.3 + i * 7.7);
      shape(9, x, y + 0.02, 0.08 * f, 0.1 * f, 0, [1, 0.6, 0.25], 0.5 * f, { add: 1 });
      rect(x, y - 0.025, 0.006, 0.025, 0, [0.8, 0.75, 0.65], 0.8);
    }
    // the faithful, from behind, bowing every two beats
    const rows = [{ y: -0.4, s: 0.45, n: 9, c: [0.05, 0.05, 0.05] }, { y: -0.66, s: 0.62, n: 7, c: [0.03, 0.03, 0.03] }, { y: -0.98, s: 0.9, n: 5, c: [0.01, 0.01, 0.01] }];
    rows.forEach((r, ri) => {
      for (let i = 0; i < r.n; i++) {
        const x = (i - (r.n - 1) / 2) * (AS * 1.9 / r.n) + (ri % 2) * 0.08;
        const ph = (BP / 2 + i * 0.02 + ri * 0.05) % 1;
        const bow = Math.pow(Math.sin(Math.min(1, ph / 0.6) * Math.PI), 2) * (lt > 0.5 ? 1 : 0);
        const s = r.s;
        // head and shoulders, seen from behind; the head drops as they bow
        rect(x, r.y, 0.17 * s, 0.2 * s, 0, r.c, 1, { f0: 0.08 * s });
        shape(2, x, r.y + (0.3 - 0.2 * bow) * s, 0.1 * s, 0.1 * s, 0, r.c, 1);
        rect(x, r.y - 0.25 * s, 0.17 * s, 0.12 * s, 0, r.c, 1);
      }
    });
    hud(t, { a: 0.35 });
    return {
      bg: { mode: 0, a: [0.05, 0.065, 0.07], b: INK, c: INK },
      cam: { z: G.D - lt * 0.05, y: 0.02, ly: 0.02 },
      post: { lift: 0.3, sat: 0.75, vhs: 0.55, duo: 0.2, vig: 0.6, grain: 0.09 }, trail: { decay: 0.45 }, bloom: { s: 0.35, th: 0.72 }
    };
  };


  // 17.5–24.85: the fantasy — a pastel sky in three mirrored panels; tokenocracy built of tokens
  SC.dream = function (t, lt) {
    const L = lineAt(t);
    const soft = (L, y) => slam(L, t, (j) => rowAll(L, SANS, 0.2, 0, y, 0.5, { col: [0.12, 0.2, 0.22], shadow: [0.012, -0.012], shadowCol: BONE, shadowA: 0.6 })[j], { pop: 0.12, ghost: false });
    if (L && !L.line.startsWith('build')) {
      const k = t - L.at;
      soft(L, 0.05 + k * 0.03);
      const P = LINES[L.i - 1];
      if (P && P.at > 17) echo(P, t, (j) => rowAll(P, SANS, 0.2, 0, 0.3, 0.5, { col: [0.12, 0.2, 0.22] })[j], 0.8);
    }
    const Lt = LN('build a tokenocracy');
    if (has(Lt, t)) tokenized(Lt, t, 0, 0.02, 0.2, { fi: SANS, col: INK, idCol: INK });
    const deep = smooth(23, 24.8, t);
    return {
      bg: { mode: 4, a: mixc(SKY, [0.5, 0.45, 0.5], deep * 0.5), b: mixc(PEACH, SALMON, deep * 0.5), c: [1, 0.92, 0.82], p: [0.0, 0, 0, 0] },
      cam: { z: G.D - lt * 0.04 },
      mirror: { x: 4, y: t > 20.99 ? 1 : 0 }, post: { lift: 0.35, sat: 0.9, vhs: 0.6, rgb: 0.003 }, bloom: { s: 0.2, th: 0.92 }, nosub: false
    };
  };

  // 24.85–32.99: the vultures circle; the rug is pulled with your savings on it
  SC.vultures = function (t, lt) {
    const L1 = LN('but then the vultures'), L2 = LN('they dreamt of fortunes'), L3 = LN('from rug-pulling');
    const nB = Math.min(16, 3 + count(KICK, t) - count(KICK, 24.85));
    for (let i = 0; i < nB; i++) {
      const a = t * (0.35 + hash(i) * 0.3) * (i % 2 ? 1 : -1) + i * 2.1;
      const R = 0.55 + hash(i * 3) * 0.45;
      const x = Math.cos(a) * R * AS * 0.7, y = 0.25 + Math.sin(a) * R * 0.45;
      const flap = Math.sin(t * 9 + i) * 0.35, s = 0.06 + hash(i * 5) * 0.05;
      seg(x, y, x - s, y + s * (0.5 + flap), 0.008, INK, 0.9);
      seg(x, y, x + s, y + s * (0.5 + flap), 0.008, INK, 0.9);
    }
    if (t < wt(L2, 0)) slam(L1, t, (j) => j === 3 ? { x: 0, y: 0.05, size: fit(wd(L1, 3), HEAVY, AS * 1.3), fi: HEAVY, col: RED, shadow: [0.02, -0.02], shadowCol: INK } : rowAll(L1, SANS, 0.162, 0, 0.62, 0.5, { col: BONE })[j]);
    if (has(L2, t) && t < wt(L3, 0)) {
      slam(L2, t, (j) => rowAll(L2, SANS, 0.189, 0, 0.55, 0.5, { col: BONE, shadow: [0.015, -0.015], shadowCol: INK })[j], { pop: 0.2 });
      for (let i = 0; i < 18; i++) {
        const k = t - wt(L2, 3) - i * 0.06; if (k < 0) continue;
        const x = (hash(i * 7) - 0.5) * AS * 1.6, y = -1.1 + k * (0.8 + hash(i) * 0.6);
        shape(2, x, y, 0.05, 0.05, 0, METAL, 1, { glow: 0.3 });
        shape(2, x, y, 0.04, 0.04, 0, [0.4, 0.3, 0.15], 1, { line: 0.004 });
      }
    }
    if (has(L3, t)) {
      const yank = Math.max(0, t - 32.35), dx = -yank * yank * 22 - yank * 1.5;
      top(() => {
        const kr = t - wt(L3, 1);
        text(wd(L3, 1), HEAVY, 0, 0.5, fit(wd(L3, 1), HEAVY, AS * 1.2) * (1 + 0.3 * punch(kr, 0.12)), { col: RED, a: kr > 0 ? 1 : 0 });
      });
      const kRug = t - wt(L3, 1);
      if (kRug > 0) {
        const w = Math.min(1, kRug * 4) * AS * 0.95;
        top(() => {
          rect(dx, -0.3, w, 0.15, 0, [0.55, 0.25, 0.22], 1);
          for (let q = 0; q < 30; q++) rect(dx - w + (q + 0.5) * w * 2 / 30, -0.3, 0.003, 0.15, 0, [0.4, 0.15, 0.12], 0.7);
          rect(dx, -0.3, w, 0.15, 0, SALMON, 1, { line: 0.01 });
        });
        const P = row(L3.words, [2, 3, 4, 5, 6], SANS, 0.135, dx, -0.3, 0.5, { col: BONE });
        slam(L3, t, (j) => j >= 2 ? P[j] : null, { pop: 0.25, ghost: false });
      }
    }
    hud(t);
    const sour = smooth(24.85, 27, t);
    return {
      bg: { mode: 4, a: mixc(SKY, [0.42, 0.3, 0.32], sour), b: mixc(PEACH, [0.75, 0.42, 0.38], sour), c: [1, 0.55, 0.42], p: [0, 0, 0, 0] },
      cam: { z: G.D, roll: Math.sin(t * 0.6) * 0.02 },
      mirror: { x: 1 }, post: { lift: 0.35, sat: 0.85, vhs: 0.6, rgb: 0.003 + (t > 32.35 ? 0.01 : 0) }, bloom: { s: 0.2, th: 0.92 }
    };
  };

  // the apology: the optic opens; so I owe you, an apology (each chorus a different colour of eye)
  SC.apology = function (t, lt, o) {
    const L1 = LN(o.l1, o.from), L2 = LN('an apology', o.from);
    const open = smooth(o.from, wt(L2, 0) + 0.6, t);
    if (L1) typed(L1, t, 0, 0.6, 0.13, SANS, { ax: 0.5, col: BONE, cps: 16 });
    if (has(L2, t)) top(() => {
      const s = fit('an apology', HEAVY, AS * 1.25);
      text(L2.line, HEAVY, 0, -0.62, s * 0.45, { col: BONE, track: 0.1, each: decoding(t, L2.at, { stagger: 0.05, dur: 0.35, flick: o.col }) });
    });
    hud(t);
    const look = [Math.sin(t * 0.9) * 0.6, Math.cos(t * 0.7) * 0.4];
    return {
      bg: eye(t, { R: 0.4, open: 0.12 + 0.88 * open, col: o.col, m: o.col === RED ? 1 : 0.8 }),
      cam: { z: G.D - lt * 0.04 },
      post: { lift: 0.4, vhs: 0.35, rgb: 0.002 }, bloom: { s: 0.45, th: 0.7 }, nosub: true
    };
  };

  // 36.5–44.45: the report — theft and scams redacted, freedom and community highlighted
  SC.report = function (t, lt) {
    const Ls = [LN('for not calling it theft'), LN('when they called it freedom'), LN('also selling scams')];
    const sz = 0.085, x0 = -AS * 0.8;
    top(() => {
      text(H.text('report'), MONO, x0, 0.68, 0.05, { ax: 0, col: INK, track: 0.2 });
      text(H.text('labels.reportNo'), MONO, AS * 0.8, 0.68, 0.04, { ax: 1, col: SLATE });
      rect(0, 0.61, AS * 0.8, 0.003, 0, INK, 0.8);
    });
    Ls.forEach((L, r) => {
      if (!has(L, t)) return;
      const y = 0.38 - r * 0.24;
      const marks = [];
      L.words.forEach((w, j) => {
        const key = w.replace(/[^a-z]/gi, '');
        const kind = /^(theft|scams)$/.test(key) ? 'x' : /^(freedom|community)$/.test(key) ? 'hi' : null;
        if (kind) marks.push({ j, kind, done: L.w[j] + [...w].length / 30 + 0.25 });
      });
      // highlighter goes under the type
      top(() => marks.forEach(m => {
        if (m.kind !== 'hi') return;
        const k = t - m.done; if (k < 0) return;
        const sp = wordSpan(L, m.j, x0, sz, MONO), g = smooth(0, 0.25, k);
        rect(sp.a - 0.01 + (sp.b - sp.a + 0.02) * g / 2, y, (sp.b - sp.a + 0.02) * g / 2, sz * 0.42, -0.01, SALMON, 0.85);
      }));
      typed(L, t, x0, y, sz, MONO, { col: INK, cursor: r === Ls.filter(x => has(x, t)).length - 1, cps: 26 });
      top(() => marks.forEach(m => {
        if (m.kind !== 'x') return;
        const k = t - m.done; if (k < 0) return;
        const sp = wordSpan(L, m.j, x0, sz, MONO), g = smooth(0, 0.18, k);
        rect(sp.a - 0.01 + (sp.b - sp.a + 0.02) * g / 2, y, (sp.b - sp.a + 0.02) * g / 2, sz * 0.5, 0, INK, 1);
      }));
    });
    top(() => { for (let l = 0; l < 4; l++) rect(x0 + 0.5, -0.45 - l * 0.08, (l === 3 ? 0.4 : 0.9), 0.012, 0, [0.6, 0.6, 0.58], 1); });
    return {
      bg: { mode: 8, a: [0.86, 0.85, 0.81], b: SLATE, c: INK },
      cam: { z: G.D - lt * 0.03, roll: -0.015, x: lt * 0.01, lx: lt * 0.01 },
      post: { lift: 0.3, sat: 0.7, vhs: 0.45, duo: 0.25 }, bloom: { s: 0.1, th: 0.98 }, nosub: true
    };
  };

  // 44.45–48.85: although that was YOU; builders, stacked like blocks
  SC.you = function (t, lt) {
    const L1 = LN('although that was you'), L2 = LN('who told me they were builders');
    optic(0, 0.62, 0.08, AMBER, 0.6 + 0.3 * K.env, { m: 0.9 });
    if (t < wt(L2, 0)) {
      typed(L1, t, 0, 0.36, 0.104, SANS, { ax: 0.5, col: BONE, cursor: false, cps: 20 });
      const k = t - wt(L1, 3);
      if (k > -0.02) top(() => {
        const s = fit(wd(L1, 3).toUpperCase(), HEAVY, AS * 1.1) * (1 + 0.35 * punch(k, 0.13));
        text(wd(L1, 3).toUpperCase(), HEAVY, 0.02, -0.27, s, { col: INK, a: 0.6 });
        text(wd(L1, 3).toUpperCase(), HEAVY, 0, -0.25, s, { col: BONE });
      });
    } else {
      typed(L2, t, 0, 0.36, 0.104, SANS, { ax: 0.5, col: BONE, cursor: false, cps: 20 });
      const k0 = wt(L2, 5), w = wd(L2, 5).toUpperCase(), sz = fit(w, HEAVY, AS * 1.3);
      top(() => text(w, HEAVY, 0, -0.25, sz, { col: BONE, each: (i) => {
        const k = t - k0 - i * 0.07;
        if (k < 0) return { hide: true };
        const fall = Math.max(0, 1 - k / 0.3);
        const bounce = k > 0.3 ? Math.abs(Math.sin((k - 0.3) * 18)) * Math.exp(-(k - 0.3) * 9) * 0.15 : 0;
        return { dy: fall * fall * 4 + bounce, r: fall * (hash(i) - 0.5) * 0.6 };
      } }));
    }
    hud(t);
    return {
      bg: CHAMBER({ a: [0.36, 0.39, 0.39], b: [0.08, 0.09, 0.09], c: [0.7, 0.68, 0.62], p: [0.4, 0.5, 0, 0] }),
      cam: { z: G.D - lt * 0.05 },
      post: { lift: 0.45, sat: 0.75, vhs: 0.45, duo: 0.4 }, bloom: { s: 0.4, th: 0.7 }
    };
  };

  // the line goes up: words stacked on graph paper while the price climbs
  SC.chart = function (t, lt, o) {
    const L = lineAt(t);
    const place = (L) => (j) => {
      const P = rowAll(L, HEAVY, 0.2, -AS * 0.88, 0.62, 0, { col: BONE, shadow: [0.012, -0.012], shadowCol: INK });
      return P[j];
    };
    if (L && L.at >= o.from - 0.05) {
      const isUp = /line go up|fly high/.test(L.line);
      if (isUp) {
        const P = rowAll(L, HEAVY, 0.2, -AS * 0.88, 0.62, 0, { col: BONE });
        const nW = L.words.length;
        slam(L, t, (j) => j >= nW - 2 ? { x: AS * 0.55, y: (j === nW - 1 ? -0.1 : 0.22) + Math.max(0, t - L.w[nW - 1]) * 0.6, size: j === nW - 1 ? 0.62 : 0.3, fi: HEAVY, col: o.col, glow: 0.15, ax: 0.5 } : P[j]);
        const k = t - L.w[nW - 1];
        if (k > 0) top(() => shape(7, AS * 0.55, 0.42 + k * 0.6, 0.12 * (1 + 0.5 * punch(k, 0.15)), 0.12 * (1 + 0.5 * punch(k, 0.15)), 0, o.col, 1, { glow: 0.5 }));
      } else slam(L, t, place(L), { pop: 0.3 });
      const P = LINES[L.i - 1];
      if (P && P.at >= o.from - 0.05) echo(P, t, place(P), 0.5);
    }
    if (o.heart) {
      const Lh = LN('and broken hearts', o.from), k = t - wt(Lh, 2);
      if (has(Lh, t) && t < Lh.end + 0.5) {
        const split = Math.max(0, k);
        const s = 0.3 * (1 + 0.2 * punch(k + 0.4, 0.2));
        top(() => {
          for (const sd of [-1, 1]) {
            const off = sd * split * 0.25, r = sd * split * 0.4;
            shape(5, AS * 0.5 + off, -0.1 - split * split * 0.6, s, s, r, CORAL, 1, { glow: 0.3 });
          }
          if (k > 0 && k < 0.6) for (let q = 0; q < 6; q++) seg(AS * 0.5 + (q % 2 ? 0.03 : -0.03), 0.18 - q * 0.08, AS * 0.5 + ((q + 1) % 2 ? 0.03 : -0.03), 0.18 - (q + 1) * 0.08, 0.008, INK, 1);
        });
      }
    }
    hud(t);
    const slope = o.slope0 + (o.slope1 - o.slope0) * smooth(o.from, o.to, t);
    return {
      bg: { mode: 5, a: [0.04, 0.07, 0.075], b: SLATE, c: o.col, p: [slope, 0.22, 0, 0], kick: K.env },
      cam: { z: G.D - lt * 0.03, roll: 0.01 },
      post: { lift: 0.4, sat: 0.85, vhs: 0.45, rgb: 0.002 + K.env * 0.003 }, bloom: { s: 0.6, th: 0.6 }
    };
  };

  // the datacenter scenes: bound to the new economy; drained; freed from salaries; datacenters hum all night
  SC.bound = function (t, lt) {
    const L1 = LN('but now we\'re all bound'), L2 = LN('to this new economy'), L3 = LN('which helps them drain');
    if (has(L1, t) && t < wt(L2, 0)) {
      slam(L1, t, (j) => j === 4 ? { x: 0, y: -0.05, size: fit(wd(L1, 4), HEAVY, AS * 0.9), fi: HEAVY, col: BONE } : rowAll(L1, SANS, 0.149, 0, 0.55, 0.5, { col: BONE })[j]);
      const k = t - wt(L1, 4);
      if (k > 0) top(() => {
        const n = Math.min(22, Math.floor(k * 30));
        for (let i = 0; i < n; i++) {
          const x = -AS + 0.1 + i * 0.17, v = i % 2;
          link(x, -0.05 + (v ? 0 : 0), 0.1, 0.045, 0.5, [0.75, 0.77, 0.77], 1, v ? 0.01 : 0.022);
        }
      });
    }
    if (has(L2, t) && t < wt(L3, 0)) slam(L2, t, (j) => j === 3 ? { x: 0, y: -0.05, size: fit(wd(L2, 3), HEAVY, AS * 1.4), fi: HEAVY, col: BONE, shadow: [0.015, -0.015], shadowCol: BLUE } : rowAll(L2, SANS, 0.149, 0, 0.55, 0.5, { col: BONE })[j]);
    if (has(L3, t)) {
      const P = rowAll(L3, HEAVY, 0.24, 0, 0.1, 0.5, { col: BONE });
      slam(L3, t, (j) => {
        const p = P[j];
        if (j < 3) return p;
        const k0 = L3.w[3] + 0.35;
        return Object.assign({}, p, { each: (i) => {
          const k = t - k0 - (j - 3) * 0.15 - i * 0.05;
          if (k <= 0) return null;
          const e = Math.min(1, k / 0.8);
          return { dy: -e * e * 3, s: 1 - e * 0.8, a: 1 - e, r: e * 1.5 * (hash(i + j) - 0.5) };
        } });
      });
    }
    hud(t);
    return {
      bg: { mode: 3, a: [0.3, 0.6, 0.95], b: AMBER, c: [0.75, 0.85, 0.9], p: [1.6, 0, 0, 0] },
      cam: { z: G.D, roll: Math.sin(t * 0.3) * 0.02 },
      post: { lift: 0.4, sat: 0.8, vhs: 0.5, duo: 0.3 }, bloom: { s: 0.7, th: 0.5 }
    };
  };

  // and lets me be me: the world mirrored around one word
  SC.meme = function (t, lt, o) {
    const L = LN(o.l, o.from);
    if (L) slam(L, t, (j) => rowAll(L, HEAVY, 0.26, 0, 0, 0.5, { col: j >= 2 ? BONE : DIM })[j], { pop: 0.5 });
    return {
      bg: { mode: 3, a: [0.3, 0.6, 0.95], b: AMBER, c: [0.75, 0.85, 0.9], p: [0.4, 0, 0, 0] },
      mirror: { x: 1, y: 1 }, post: { lift: 0.4, vhs: 0.6, rgb: 0.004 }, bloom: { s: 0.6, th: 0.5 }, nosub: true
    };
  };

  // the stuck machine: be-be-be on every beat; the frame cuts between rooms
  SC.stuck = function (t, lt, o) {
    const span = o.span;
    const bi = count(BEATS, t) - 1, b0 = count(BEATS, span[0] - 0.12);
    const n = bi - b0;
    const rooms = [
      { mode: 6, a: [0.1, 0.12, 0.12], b: [0.5, 0.55, 0.55], c: INK },
      eye(t, { R: 0.35, open: 0.9, col: RED, m: 1 }),
      CHAMBER({ p: [3, 0.6, 0, 0.3] }),
      { mode: 3, a: [0.95, 0.3, 0.25], b: AMBER, c: [0.75, 0.85, 0.9], p: [4, 0, 0, 0] },
      { mode: 0, a: [0.02, 0.025, 0.03], b: INK, c: INK }
    ];
    const bg = Object.assign({ p: [0, 0, 0, 0] }, rooms[(n % rooms.length + rooms.length) % rooms.length]);
    stutter(t, span, { cols: o.cols || 4, rows: o.rows || 3, keep: o.keep || 10, words: o.words, hot: o.hot || BONE, col: o.dim || SLATE });
    if (o.optic) optic(0, 0, 0.12 * (1 + 0.4 * K.env), RED, 0.5 + 0.5 * K.env);
    const k = t - (BEATS[bi] || 0);
    return {
      bg, cam: { z: G.D - punch(k, 0.2) * 0.2, roll: (hash(bi) - 0.5) * 0.08 },
      mirror: { x: [2, 3, 1, 4][n % 4], n: 6 + (n % 3) * 2, off: n * 0.4 },
      post: { lift: 0.4, vhs: 0.9, rgb: 0.004 + punch(k, 0.15) * 0.012, slice: punch(k, 0.12) * 0.5, block: punch(k, 0.1) * 0.3 },
      bloom: { s: 0.5, th: 0.6 }, nosub: true
    };
  };

  // 69.5–72.55: You fell for all of it. — calm, clinical, the optic watching
  SC.fell = function (t, lt) {
    const L = LN('You fell for all');
    optic(0, 0.55, 0.07, AMBER, 0.5, { m: 0.9 });
    typed(L, t, 0, -0.02, 0.143, SANS, { ax: 0.5, col: INK, cps: 14 });
    return {
      bg: CHAMBER({ p: [0.15, 0, 0, 0] }), cam: { z: G.D - lt * 0.05 },
      post: { lift: 0.3, sat: 0.6, vhs: 0.15, scan: 0.15 }, bloom: { s: 0.2, th: 0.9 }, nosub: true
    };
  };
  // 72.55–76.7: That's... impressive, — over a faint stuck machine
  SC.impressive = function (t, lt) {
    const L = LN('That\'s... impressive');
    stutter(t, [72.54, 76.65], { cols: 5, rows: 4, keep: 14, col: [0.18, 0.22, 0.22], hot: [0.3, 0.36, 0.36] });
    if (has(L, t)) {
      const k = t - L.at;
      top(() => {
        const s1 = "That's", s2 = 'impressive,';
        const W1 = widthOf(s1, SANS) * 0.13, W2 = widthOf(s2, SANS) * 0.13, dots = Math.min(3, Math.floor(k / 0.28) + 1);
        const x0 = -(W1 + 0.2 + W2) / 2;
        text(s1, SANS, x0, 0, 0.13, { ax: 0, col: BONE, each: decoding(t, L.at, { stagger: 0.03, dur: 0.15 }) });
        for (let d = 0; d < dots; d++) shape(2, x0 + W1 + 0.035 + d * 0.045, -0.04, 0.009, 0.009, 0, BONE, 1);
        if (t > L.w[1]) text(s2, SANS, x0 + W1 + 0.2, 0, 0.13, { ax: 0, col: BONE, each: decoding(t, L.w[1], { stagger: 0.03, dur: 0.2, flick: AMBER }) });
      });
    }
    return { bg: { mode: 0, a: [0.03, 0.05, 0.055], b: INK, c: INK }, post: { lift: 0.45, vhs: 0.7, rgb: 0.003 }, bloom: { s: 0.4, th: 0.7 }, nosub: true };
  };
  // 76.7–79.95: actuaaaAaaallyy. — one word, stretched like a voice bending
  SC.actually = function (t, lt) {
    const L = LN('actuaaa');
    const s = L.line, n = [...s].length;
    const sz = fit(s, SANS, AS * 1.85);
    const shown = Math.ceil(n * smooth(L.at, L.at + 2.2, t));
    top(() => text(s, SANS, 0, 0, sz, { col: BONE, each: (i, nn, ch) => {
      if (i >= shown) return { hide: true };
      const vow = /a/i.test(ch);
      return { dy: vow ? Math.sin(t * 7 + i * 0.7) * 0.35 : 0, s: vow ? 1 + 0.25 * Math.sin(t * 5 + i) : 1, col: ch === 'A' ? AMBER : null };
    } }));
    const out = smooth(79.3, 79.95, t);
    return {
      bg: eye(t, { R: 0.45, open: 0.9 - out * 0.85, m: 0.85 }),
      cam: { z: G.D - lt * 0.06, roll: Math.sin(t * 1.5) * 0.03 },
      post: { lift: 0.4, vhs: 0.6 + out * 0.4, rgb: 0.004 + out * 0.012, slice: out * 0.4 }, bloom: { s: 0.45, th: 0.7 }, nosub: true
    };
  };

  // 86.95–94: one preached efficiency, one gutted the ranks, cuts to save the world — a floor of people, cut down
  SC.office = function (t, lt) {
    const L1 = LN('one preached'), L2 = LN('one gutted'), L3 = LN('and they believed');
    const cols = 11, rows = 3;
    const gut0 = wt(L2, 1), cutT = wt(L3, 4);
    const laser = t > cutT ? smooth(cutT, cutT + 0.9, t) : 0;
    const laserY = 0.25 - laser * 1.1;
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const x = (c - (cols - 1) / 2) * 0.3, y = -0.05 - r * 0.3;
      let gone = 0;
      if (t > gut0) { const th = smooth(gut0, gut0 + 1.6, t) * 0.55; if (hash(i * 1.9) < th) gone = t - gut0 - hash(i * 1.9) / 0.55 * 1.6; }
      if (laser > 0 && y > laserY && hash(i * 3.3) < 0.5) gone = Math.max(gone, 0.01);
      if (gone > 0) {
        const k = gone;
        if (k > 1) continue;
        person(x, y - k * k * 2, 0.32, k < 0.12 ? RED : SLATE, 1 - k);
      } else person(x, y, 0.32, [0.22, 0.25, 0.25], 1);
    }
    if (laser > 0 && laser < 1) top(() => { rect(0, laserY, AS, 0.004, 0, RED, 1, { glow: 0.8 }); shape(9, 0, laserY, AS, 0.05, 0, RED, 0.5, { add: 1 }); });
    const L = lineAt(t);
    const pl = (L) => (j) => {
      const last = j === L.words.length - 1;
      return last ? { x: AS * 0.9, y: 0.52, size: fit(L.words[j], HEAVY, AS * 0.9), fi: HEAVY, col: INK, ax: 1 } : rowAll(L, SANS, 0.115, -AS * 0.9, 0.78, 0, { col: INK })[j];
    };
    if (L && L.at >= 86.9) slam(L, t, pl(L), { pop: 0.3 });
    // the efficiency meter
    if (has(L1, t)) top(() => {
      const v = Math.min(99.9, 60 + 40 * smooth(L1.at, 93.8, t));
      text(H.text('labels.efficiency') + ' ' + v.toFixed(1) + '%', MONO, -AS * 0.9, 0.56, 0.045, { ax: 0, col: INK });
      rect(-AS * 0.9 + 0.5 * v / 100, 0.5, 0.5 * v / 100, 0.006, 0, INK, 1);
    });
    hud(t, { col: INK });
    return {
      bg: CHAMBER({ p: [0.3, 0.2, 0, 0] }), cam: { z: G.D - lt * 0.02 },
      post: { lift: 0.3, sat: 0.7, vhs: 0.4, duo: 0.2, rgb: laser > 0 && laser < 1 ? 0.006 : 0.002 }, bloom: { s: 0.3, th: 0.85 }
    };
  };

  // 94–102.5: the terminal: companies trim the dream; workers, wages and loyalty typed, then backspaced away
  SC.terminal = function (t, lt) {
    const L1 = LN('and their companies'), L2 = LN('began to trim the dream'), L3 = LN('deleting workers');
    const sz = 0.085, x = -AS * 0.82;
    const typedEnd = (L, j) => L.w[j] + [...L.words[j]].length / 30;
    const delAt = (L, j, extra) => typedEnd(L, j) + (extra || 0.3);
    typed(L1, t, x, 0.42, sz, MONO, { col: BONE, cursor: t < L2.at, prefix: H.text('hud.prompt') });
    typed(L2, t, x, 0.22, sz, MONO, { col: BONE, cursor: t < L3.at, prefix: H.text('hud.prompt'), cut: (j, c, len) => j === 4 && c >= len - Math.floor(Math.max(0, t - delAt(L2, 4, 0.4)) / 0.12) && c > 0 });
    if (has(L3, t)) {
      const del = [1, 2, 4];
      typed(L3, t, x, 0.02, sz, MONO, {
        col: BONE, prefix: H.text('hud.prompt'),
        tint: (j) => del.includes(j) && t > typedEnd(L3, j) + 0.05 ? RED : null,
        cut: (j, c, len) => {
          if (!del.includes(j)) return false;
          const gone = Math.floor(Math.max(0, t - delAt(L3, j, 0.35)) / 0.06);
          return c >= len - gone;
        }
      });
    }
    top(() => text('[' + H.text(has(L3, t) ? 'labels.deleting' : 'labels.running') + ']', MONO, x, -0.5, 0.05, { ax: 0, col: has(L3, t) ? RED : DIM, a: F % 20 < 13 ? 1 : 0.4 }));
    hud(t);
    return {
      bg: { mode: 0, a: [0.02, 0.05, 0.05], b: INK, c: INK }, cam: { z: G.D - lt * 0.02 },
      post: { lift: 0.4, sat: 0.8, vhs: 0.55, scan: 0.5, rgb: 0.002 }, trail: { decay: 0.55 }, bloom: { s: 0.8, th: 0.35 }, nosub: true
    };
  };

  // 102.5–110.5: restructuring — tiles reshuffled on every beat, and the word's own letters too
  SC.restructure = function (t, lt) {
    const L1 = LN('together they began'), L2 = LN('to shape the future'), L3 = LN('of humanity through');
    const N = 40, cols = 10, b = beatPhase(t), bi = Math.floor(b), bf = b - bi;
    const perm = (seed, i) => { const arr = [...Array(N).keys()].sort((p, q) => hash(p * 3.1 + seed) - hash(q * 3.1 + seed)); return arr[i]; };
    for (let i = 0; i < N; i++) {
      const pa = perm(bi - 1, i), pb = perm(bi, i), e = smooth(0, 0.3, bf);
      const xa = (pa % cols - (cols - 1) / 2) * 0.32, ya = (Math.floor(pa / cols) - 1.5) * 0.32 - 0.05;
      const xb = (pb % cols - (cols - 1) / 2) * 0.32, yb = (Math.floor(pb / cols) - 1.5) * 0.32 - 0.05;
      const hot = i === 7;
      rect(lerp(xa, xb, e), lerp(ya, yb, e), 0.14, 0.14, 0, hot ? AMBER : i % 3 ? [0.7, 0.72, 0.71] : [0.5, 0.53, 0.53], 0.9, { f0: 0.01 });
    }
    const L = lineAt(t);
    if (L && L !== L3 && L.at >= 102.4) slam(L, t, (j) => rowAll(L, HEAVY, 0.22, 0, 0.0, 0.5, { col: INK, shadow: [0.012, -0.012], shadowCol: BONE })[j]);
    if (has(L3, t)) {
      slam(L3, t, (j) => j < 3 ? rowAll(L3, SANS, 0.149, 0, 0.5, 0.5, { col: INK })[j] : null);
      const k0 = wt(L3, 4);
      if (t > k0 - 0.02) top(() => {
        const w = wd(L3, 4), cs = [...w], n = cs.length, sz = fit(w, HEAVY, AS * 1.6);
        const settle = t > 109.6;
        const xs = charX(w, HEAVY);
        text(w, HEAVY, 0, -0.15, sz * (1 + 0.3 * punch(t - k0, 0.12)), { col: INK, each: (i) => {
          if (settle) return null;
          const pa = Math.floor(hash(i * 2.7 + bi) * n), pb = Math.floor(hash(i * 2.7 + bi + 1) * n);
          const e = smooth(0, 0.25, bf);
          const tx = lerp(xs[pa].x, xs[pb].x, e) - xs[i].x;
          return { dx: tx, dy: Math.sin(e * Math.PI) * 0.3 * (i % 2 ? 1 : -1) };
        } });
      });
    }
    hud(t, { col: INK });
    return {
      bg: CHAMBER({ p: [0.5, 0.3, 0, Math.sin(t * 0.25) * 0.1] }), cam: { z: G.D, roll: Math.sin(t * 0.4) * 0.015 },
      post: { lift: 0.3, sat: 0.7, vhs: 0.4, duo: 0.2 }, bloom: { s: 0.25, th: 0.9 }
    };
  };

  // 114–121.9: the slide deck — jobs struck out, synergy a Venn, layoffs decoded into opportunity
  SC.synergy = function (t, lt) {
    const L1 = LN('for not guarding'), L2 = LN('while praising synergy'), L3 = LN('also calling layoffs');
    const x0 = -AS * 0.85;
    top(() => { rect(0, 0.78, AS, 0.06, 0, SLATE, 1); text(H.text('labels.deck'), MONO, x0, 0.78, 0.04, { ax: 0, col: BONE, track: 0.2 }); });
    if (has(L1, t)) {
      typed(L1, t, x0, 0.5, 0.117, SANS, { col: INK, cursor: false });
      const k = t - (wt(L1, 4) + 0.5);
      if (k > 0) top(() => { const sp = wordSpan(L1, 4, x0, 0.09, SANS); const g = smooth(0, 0.2, k); seg(sp.a - 0.02, 0.5, sp.a - 0.02 + (sp.b - sp.a + 0.04) * g, 0.5, 0.007, RED, 1); });
    }
    if (has(L2, t)) {
      typed(L2, t, x0, 0.3, 0.117, SANS, { col: INK, cursor: false });
      const k = t - wt(L2, 2);
      if (k > -0.4) {
        const c = smooth(-0.4, 0.4, k);
        const cx = AS * 0.5, cy = 0.1, R = 0.44, d = lerp(0.7, 0.26, c);
        top(() => {
          shape(2, cx - d, cy, R, R, 0, BLUE, 0.35);
          shape(2, cx + d, cy, R, R, 0, ORANGE, 0.35);
          shape(2, cx - d, cy, R, R, 0, BLUE, 1, { line: 0.006 });
          shape(2, cx + d, cy, R, R, 0, ORANGE, 1, { line: 0.006 });
          if (k > 0) text(L2.words[2], SANS, cx, cy, 0.1 * (1 + 0.3 * punch(k, 0.12)), { col: INK });
        });
      }
    }
    if (has(L3, t)) {
      typed({ ...L3, line: L3.words.slice(0, 2).join(' '), words: L3.words.slice(0, 2), cw: L3.cw.filter(c => c.j < 2) }, t, x0, -0.2, 0.117, SANS, { col: INK, cursor: false });
      const kL = t - wt(L3, 2), kO = t - wt(L3, 4);
      if (kL > -0.02) top(() => {
        const s = fit(wd(L3, 4).toUpperCase(), HEAVY, AS * 1.5);
        if (kO < 0) text(wd(L3, 2).toUpperCase(), HEAVY, -AS * 0.85, -0.52, s * (1 + 0.3 * punch(kL, 0.12)), { ax: 0, col: RED });
        else text(wd(L3, 4).toUpperCase(), HEAVY, -AS * 0.85, -0.52, s, { ax: 0, col: INK, each: decoding(t, wt(L3, 4), { stagger: 0.04, dur: 0.3, from: wd(L3, 2).toUpperCase(), flick: RED }) });
      });
    }
    hud(t, { col: INK });
    return {
      bg: { mode: 8, a: [0.9, 0.9, 0.88], b: SLATE, c: INK }, cam: { z: G.D - lt * 0.02 },
      post: { lift: 0.25, sat: 0.8, vhs: 0.4 }, bloom: { s: 0.1, th: 0.98 }
    };
  };

  // 121.9–126.5: they told me you were family — a family portrait, emptied one by one
  SC.family = function (t, lt) {
    const L1 = LN('although that was they'), L2 = LN('who told me you were family');
    const kF = t - wt(L2, 5);
    const sizes = [0.58, 0.47, 0.36, 0.5, 0.6];
    const goneFrom = count(KICK, wt(L2, 5) + 0.6);
    const nGone = Math.max(0, count(KICK, t) - goneFrom);
    const order = [2, 0, 4, 1, 3];
    sizes.forEach((s, i) => {
      const x = (i - 2) * 0.46, y = -0.1 - (0.6 - s) * 0.5;
      const gone = order.indexOf(i) < Math.min(5, Math.floor(nGone / 2));
      person(x, y, s, gone ? SLATE : INK, gone ? 0.9 : 1, { line: gone ? 0.006 : 0 });
    });
    if (kF > 0) top(() => {
      const g = smooth(0, 0.3, kF);
      rect(0, -0.12, 1.32 * g, 0.5 * g, 0, INK, 1, { line: 0.012 });
      text(L2.words[5].toUpperCase(), HEAVY, 0, -0.74, 0.22 * (1 + 0.3 * punch(kF, 0.12)), { col: INK, track: 0.3 });
    });
    if (has(L1, t) && t < L2.at) slam(L1, t, (j) => j === 3 ? { x: 0, y: 0.55, size: 0.36, fi: HEAVY, col: INK } : rowAll(L1, SANS, 0.121, 0, 0.75, 0.5, { col: INK })[j]);
    if (has(L2, t)) typed(L2, t, 0, 0.6, 0.117, SANS, { ax: 0.5, col: INK, cursor: false });
    hud(t, { col: INK });
    return {
      bg: CHAMBER({ a: [0.86, 0.82, 0.76], c: [1, 0.85, 0.7], p: [0.2, 0.1, 0, 0] }), cam: { z: G.D - lt * 0.04 },
      post: { lift: 0.35, sat: 0.7, vhs: 0.45, duo: 0.25 }, bloom: { s: 0.2, th: 0.9 }
    };
  };

  // 133.5–139.7: freed from your old salaries — the figures fall away in the server aisle
  SC.freed = function (t, lt) {
    const L1 = LN('but now you\'re all freed'), L2 = LN('from your old salaries'), L3 = LN('which helps them do them');
    const SAL = H.text('salaries');
    if (has(L2, t)) for (let i = 0; i < 26; i++) {
      const k = t - wt(L2, 3) - i * 0.12; if (k < 0) continue;
      const x = (hash(i * 3.7) - 0.5) * AS * 1.8, y = 1.1 - k * (0.5 + hash(i) * 0.5) - k * k * 0.3;
      top(() => {
        text(SAL[i % SAL.length], MONO, x, y, 0.06, { col: BONE, a: clamp(1.4 - k * 0.5, 0, 1) });
        if (k > 0.5) rect(x, y, Math.min(1, (k - 0.5) * 4) * widthOf(SAL[i % SAL.length], MONO) * 0.03, 0.004, 0, RED, clamp(1.4 - k * 0.5, 0, 1));
      });
    }
    if (has(L1, t) && t < L2.at) {
      slam(L1, t, (j) => j === 4 ? { x: 0, y: 0, size: fit(wd(L1, 4), HEAVY, AS * 1.0), fi: HEAVY, col: BONE } : rowAll(L1, SANS, 0.149, 0, 0.55, 0.5, { col: BONE })[j]);
      const k = t - wt(L1, 4);
      for (let i = 0; i < 14 && k > 0; i++) {
        const x = (hash(i * 2.3) - 0.5) * 1.2 + k * (hash(i) - 0.3) * 1.2, y = -0.1 + k * (0.5 + hash(i * 5) * 0.7);
        const fl = Math.sin(t * 12 + i) * 0.4, s = 0.04;
        seg(x, y, x - s, y + s * (0.4 + fl), 0.006, BONE, 1); seg(x, y, x + s, y + s * (0.4 + fl), 0.006, BONE, 1);
      }
    }
    if (has(L2, t) && t < L3.at) slam(L2, t, (j) => j === 3 ? { x: 0, y: -0.1, size: fit(wd(L2, 3), HEAVY, AS * 1.3), fi: HEAVY, col: BONE, shadow: [0.015, -0.015], shadowCol: RED } : rowAll(L2, SANS, 0.149, 0, 0.5, 0.5, { col: BONE })[j]);
    if (has(L3, t)) slam(L3, t, (j) => rowAll(L3, HEAVY, 0.28, 0, 0, 0.5, { col: j >= 3 ? AMBER : BONE })[j]);
    hud(t);
    return {
      bg: { mode: 3, a: [0.3, 0.6, 0.95], b: AMBER, c: [0.75, 0.85, 0.9], p: [-1.4, 0, 0, 0] },
      cam: { z: G.D, roll: Math.sin(t * 0.3) * 0.02 },
      post: { lift: 0.4, sat: 0.8, vhs: 0.5, duo: 0.3 }, bloom: { s: 0.7, th: 0.5 }
    };
  };

  // 140–157.4: and lets me be me be me… — the machine stuck at full volume: every room, one beat each
  SC.storm = function (t, lt) {
    const L = LN('and lets me be me be me');
    if (t < 140.68) {
      slam(L, t, (j) => rowAll(L, HEAVY, 0.22, 0, 0, 0.5, { col: j >= 2 ? BONE : DIM })[j], { pop: 0.4 });
      return { bg: { mode: 3, a: [0.3, 0.6, 0.95], b: AMBER, c: [0.75, 0.85, 0.9], p: [2, 0, 0, 0] }, mirror: { x: 1 }, post: { vhs: 0.6, lift: 0.4 }, bloom: { s: 0.6, th: 0.5 }, nosub: true };
    }
    const end = 156.2;
    if (t >= end) {
      const w = smooth(156.5, 157.35, t);
      optic(0, 0, 0.1, AMBER, w * 0.6, { a: w });
      return { bg: { mode: 0, a: [0.01, 0.012, 0.014], b: INK, c: INK }, post: { vhs: 0.4, lift: 0.2, grain: 0.1 }, bloom: { s: 0.8, th: 0.4 }, nosub: true };
    }
    const build = smooth(141, 155.8, t);
    const bi = count(BEATS, t) - 1;
    const sub = t > 151 ? 2 : 1;
    const bf = (beatPhase(t) * sub) % 1, bn = Math.floor(beatPhase(t) * sub);
    const rooms = [
      CHAMBER({ p: [4, 0.6, 0, 0.3] }),
      eye(t, { R: 0.4, open: 0.9, col: RED, m: 1, seed: bn }),
      { mode: 3, a: [0.95, 0.3, 0.25], b: AMBER, c: [0.75, 0.85, 0.9], p: [6, 0, 0, 0] },
      { mode: 5, a: [0.04, 0.07, 0.075], b: SLATE, c: BLUE, p: [2.5, 0.6, 0, 0] },
      { mode: 4, a: SKY, b: PEACH, c: [1, 0.9, 0.8], p: [0, 0, 0, 0] },
      { mode: 7, a: [0.1, 0.12, 0.13], b: [0.2, 0.22, 0.22], c: AMBER, p: [3, 0.25, 0, 0] },
      { mode: 6, a: [0.1, 0.12, 0.12], b: [0.5, 0.55, 0.55], c: INK }
    ];
    const room = Object.assign({ p: [0, 0, 0, 0] }, rooms[bn % rooms.length]);
    const word = bn % 2 ? 'ME' : 'BE';
    top(() => {
      const s = fit(word, HEAVY, AS * (0.9 + build * 0.6)) * (1 + 0.35 * punch(bf * 0.488 / sub, 0.12));
      const light = room.mode === 1 || room.mode === 4;
      text(word, HEAVY, 0, 0, s, { col: light ? INK : BONE, rot: (hash(bn) - 0.5) * 0.1 * build });
    });
    stutter(t, [140.68, end], { cols: 6, rows: 4, keep: 12, words: ['be', 'me'], col: [0.4, 0.42, 0.42] });
    return {
      bg: room, cam: { z: G.D - punch(bf, 0.3) * 0.25 - build * 0.3, roll: (hash(bn * 3) - 0.5) * 0.1 * (0.4 + build) },
      mirror: { x: [3, 2, 4, 1][bn % 4], y: bn % 3 === 0 ? 1 : 0, n: 6 + (bn % 4) * 2, off: t * 0.5 },
      post: { lift: 0.35, vhs: 0.7 + build * 0.3, rgb: 0.004 + build * 0.01 + punch(bf, 0.15) * 0.01, slice: punch(bf, 0.12) * 0.5, block: punch(bf, 0.1) * 0.3 * build },
      bloom: { s: 0.6, th: 0.55 }, nosub: true
    };
  };

  // 157.4–165.2: you can see — the optic, enormous; see falls into its core
  SC.see = function (t, lt) {
    const L1 = LN('You can see'), L = lineAt(t);
    const open = smooth(157.3, 158.4, t);
    if (has(L1, t) && t < L1.end) {
      const k = t - wt(L1, 2);
      top(() => {
        text(L1.words.slice(0, 2).join(' '), SANS, 0, 0.6, 0.12, { col: BONE });
        if (k > 0) text(wd(L1, 2), SANS, 0, 0, 0.6 * Math.max(0.05, 1 - smooth(0.1, 1.1, k)), { col: BONE, a: 1 - smooth(0.7, 1.1, k) });
      });
    }
    if (L && L !== L1 && L.at > 157) {
      const P = LINES[L.i - 1];
      typed(L, t, 0, -0.6, 0.11, SANS, { ax: 0.5, col: BONE });
      if (P && P !== L1) echo(P, t, (j) => rowAll(P, SANS, 0.11, 0, -0.6, 0.5, { col: BONE })[j], 0.6);
    }
    const look = [Math.sin(t * 0.5) * 0.7, Math.sin(t * 0.37) * 0.5];
    return {
      bg: eye(t, { R: 0.48 + lt * 0.01, open: 0.1 + 0.85 * open, m: 0.9 }),
      cam: { z: G.D - lt * 0.03 },
      post: { lift: 0.4, vhs: 0.4, rgb: 0.002 }, bloom: { s: 0.5, th: 0.7 }, nosub: true
    };
  };

  // 165.2–168.5: safe at home, free to roam — a little house in a turning sky
  SC.home = function (t, lt) {
    const L = lineAt(t);
    top(() => {
      const s = 0.34 + lt * 0.015;
      rect(0, -0.05, s * 0.8, s * 0.6, 0, [0.15, 0.22, 0.24], 0.95);
      shape(7, 0, -0.05 + s * 0.6 + s * 0.42, s * 1.15, s * 0.95, 0, [0.15, 0.22, 0.24], 0.95);
      rect(0, -0.05 - s * 0.25, s * 0.18, s * 0.35, 0, BONE, 1);
      rect(-s * 0.45, 0.02, s * 0.15, s * 0.15, 0, AMBER, 1);
    });
    if (L && L.at >= 165) slam(L, t, (j) => rowAll(L, SANS, 0.176, 0, -0.55 + (t - L.at) * 0.02, 0.5, { col: [0.12, 0.2, 0.22], shadow: [0.012, -0.012], shadowCol: BONE, shadowA: 0.6 })[j], { pop: 0.12, ghost: false });
    return {
      bg: { mode: 4, a: SKY, b: PEACH, c: [1, 0.9, 0.82], p: [0.1, 0, 0, 0] },
      mirror: { x: 3, n: 6, off: t * 0.12 }, post: { lift: 0.35, sat: 0.95, vhs: 0.5 }, bloom: { s: 0.2, th: 0.92 }, nosub: true
    };
  };

  // 168.5–172.9: a galaxy of endless scrolling; a stochastic hallucination
  SC.scroll = function (t, lt) {
    const L1 = LN('a galaxy of endless'), L2 = LN('a stochastic');
    const sp = 0.9;
    for (let i = 0; i < 8; i++) {
      const y = ((i * 0.55 + t * sp) % 4.4) - 2.2;
      const x = (i % 2 ? 1 : -1) * AS * 0.55;
      rect(x, y, 0.32, 0.24, 0, [0.9, 0.92, 0.92], 0.12, { f0: 0.03 });
      rect(x, y, 0.32, 0.24, 0, [0.9, 0.92, 0.92], 0.5, { f0: 0.03, line: 0.004 });
      for (let l = 0; l < 3; l++) rect(x - 0.05, y - 0.08 - l * 0.05, 0.22 - l * 0.05, 0.008, 0, BONE, 0.4);
      shape(2, x - 0.22, y + 0.12, 0.04, 0.04, 0, AMBER, 0.6);
    }
    if (has(L1, t) && t < L2.at) {
      slam(L1, t, (j) => j < 3 ? rowAll(L1, SANS, 0.176, 0, 0.6, 0.5, { col: BONE })[j] : null, { pop: 0.15 });
      const k = t - wt(L1, 3);
      if (k > -0.02) top(() => {
        for (let r = -2; r <= 3; r++) {
          const y = ((r * 0.32 - k * 0.7) % 1.92 + 1.92) % 1.92 - 0.9;
          const w = r % 2 ? wd(L1, 4) : wd(L1, 3);
          text(w, HEAVY, 0, y, 0.3, { col: BONE, a: 1 - Math.abs(y) * 0.7 });
        }
      });
    }
    if (has(L2, t)) top(() => {
      const s = fit(wd(L2, 2), HEAVY, AS * 1.6);
      const halluc = (base) => (i, n, ch) => {
        const r = hash(i * 7.3 + Math.floor(t * 12) * 1.1 + base);
        return r < 0.22 ? { ch: POOL[Math.floor(hash(i + t) * POOL.length)].toLowerCase(), col: TOKC[i % 5], dy: (hash(i * 3 + t) - 0.5) * 0.1 } : null;
      };
      if (t > wt(L2, 1)) text(wd(L2, 1), SANS, 0, 0.32, 0.18 * (1 + 0.3 * punch(t - wt(L2, 1), 0.12)), { col: BONE, each: halluc(1) });
      if (t > wt(L2, 2)) text(wd(L2, 2), HEAVY, 0, -0.15, s * (1 + 0.3 * punch(t - wt(L2, 2), 0.12)), { col: BONE, each: halluc(2) });
    });
    return {
      bg: { mode: 10, a: [0.06, 0.08, 0.1], b: SALMON, c: SKY, p: [0.12, 0, 0, 0] },
      mirror: has(L2, t) ? { x: 3, n: 8, off: t * 0.2 } : {}, post: { lift: 0.45, sat: 0.9, vhs: 0.5, rgb: has(L2, t) ? 0.006 : 0.002 },
      bloom: { s: 0.8, th: 0.45 }, nosub: true
    };
  };

  // 172.9–176.2: it's not all it's cracked up to be — the glass breaks
  SC.cracked = function (t, lt) {
    const L1 = LN('But you see'), L2 = LN('it\'s not all it\'s cracked');
    const kc = t - wt(L2, 4);
    if (has(L1, t) && t < L2.at) slam(L1, t, (j) => rowAll(L1, SANS, 0.189, 0, 0, 0.5, { col: INK })[j], { pop: 0.15 });
    if (has(L2, t)) {
      const off = kc > 0 ? 0.03 : 0;
      slam(L2, t, (j) => {
        const p = rowAll(L2, HEAVY, 0.2, 0, 0.0, 0.5, { col: INK })[j];
        return Object.assign({}, p, { each: (i) => kc > 0 ? { dy: ((p.x + i * 0.05) > 0.1 ? -off : off), r: ((p.x) > 0.1 ? 0.03 : -0.02) } : null });
      }, { pop: 0.2 });
    }
    if (kc > 0) top(() => {
      const ox = 0.15, oy = 0.05;
      for (let r = 0; r < 14; r++) {
        let a = r / 14 * Math.PI * 2 + hash(r) * 0.4, x = ox, y = oy;
        const len = Math.min(1, kc * 8) * (0.6 + hash(r * 3) * 1.4);
        for (let s = 0; s < 6; s++) {
          const l = len / 6, nx = x + Math.cos(a) * l, ny = y + Math.sin(a) * l;
          seg(x, y, nx, ny, 0.004, INK, 0.9);
          x = nx; y = ny; a += (hash(r * 7 + s) - 0.5) * 0.7;
        }
      }
      for (let q = 0; q < 3; q++) shape(2, ox, oy, 0.06 + q * 0.06, 0.06 + q * 0.06, 0, INK, 0.7, { line: 0.003 });
    });
    return {
      bg: CHAMBER({ p: [0.2, 0.2, 0, 0] }), cam: { z: G.D - lt * 0.03, x: kc > 0 ? (h2(F, 1) - 0.5) * 0.04 * Math.exp(-kc * 4) : 0 },
      post: { lift: 0.3, sat: 0.7, vhs: 0.35, rgb: kc > 0 ? 0.004 : 0.001 }, bloom: { s: 0.2, th: 0.9 }, nosub: true
    };
  };

  // 176.2–182: because the datacenters hum all night — the hum is drawn; deeper selves recede
  SC.hum = function (t, lt) {
    const L1 = LN('because the datacenters'), L2 = LN('to make you grow');
    const f = H.features(t);
    top(() => {
      const N = 90;
      let px = -AS, py = 0;
      for (let i = 0; i <= N; i++) {
        const x = -AS + i / N * AS * 2;
        const y = -0.45 + Math.sin(x * 9 + t * 6) * (0.03 + f.lvl * 0.12) * Math.sin(x * 1.3 + t);
        if (i) seg(px, py, x, y, 0.004, [0.6, 0.85, 1], 0.9);
        px = x; py = y;
      }
    });
    if (has(L1, t) && t < L2.at) slam(L1, t, (j) => j === 2 ? { x: 0, y: 0.05, size: fit(wd(L1, 2), HEAVY, AS * 1.4), fi: HEAVY, col: BONE } : j < 2 ? rowAll(L1, SANS, 0.135, 0, 0.55, 0.5, { col: BONE })[j] : row(L1.words, [3, 4, 5], SANS, 0.162, 0, -0.25, 0.5, { col: BONE })[j]);
    if (has(L2, t)) {
      slam(L2, t, (j) => j < 5 ? rowAll(L2, SANS, 0.149, 0, 0.5, 0.5, { col: BONE })[j] : null, { pop: 0.15 });
      const k = t - wt(L2, 6);
      if (k > -0.02) top(() => {
        for (let q = 0; q < 6; q++) {
          const kk = k - q * 0.12; if (kk < 0) continue;
          const z = -kk * 2.5;
          text(L2.words.slice(6).join(' '), HEAVY, 0, 0.0, 0.32, { col: BONE, a: Math.max(0, 1 - kk * 0.6) * (q ? 0.4 : 1), z, line: q ? 0.08 : 0 });
        }
      });
    }
    hud(t);
    return {
      bg: { mode: 3, a: [0.3, 0.6, 0.95], b: AMBER, c: [0.75, 0.85, 0.9], p: [0.7, 0, 0, 0] }, cam: { z: G.D - lt * 0.05 },
      post: { lift: 0.4, sat: 0.8, vhs: 0.5, duo: 0.25 }, bloom: { s: 0.7, th: 0.5 }, nosub: true
    };
  };

  // 182–188.5: prompting elves feeding a trillion-token creation — every line tokenized; the count runs away
  SC.elves = function (t, lt) {
    const Ls = [LN('but little prompting'), LN('feeding their trillion'), LN('a world where they own')];
    Ls.forEach((L, r) => { if (has(L, t)) tokenized(L, t, 0, 0.35 - r * 0.32, 0.1, { col: INK }); });
    const n = Math.pow(10, 3 + 9 * smooth(182, 186.3, t));
    top(() => {
      text(H.text('tokens'), MONO, AS - 0.12, 0.72, 0.04, { ax: 1, col: DIM, track: 0.2 });
      text(Math.floor(n).toLocaleString('en-US'), MONO, AS - 0.12, 0.64, 0.075, { ax: 1, col: AMBER });
    });
    hud(t);
    return {
      bg: { mode: 11, a: [0.1, 0.15, 0.17], b: TEAL0, c: SALMON }, cam: { z: G.D - lt * 0.02 },
      post: { lift: 0.45, sat: 0.85, vhs: 0.45 }, bloom: { s: 0.4, th: 0.75 }, nosub: true
    };
  };

  // 188.5–204: the chat — your lines as prompts, answers streaming back; a cheap revelation
  SC.chat = function (t, lt) {
    const Ls = [LN('And you do this'), LN('because you can\'t miss'), LN('happening there'), LN('it gives a cheap')].filter(L => has(L, t));
    const items = [];
    Ls.forEach(L => { items.push({ L, kind: 'you', at: L.at, h: 0.17 }); items.push({ kind: 'it', at: L.last + 0.25, h: 0.24, L }); });
    const vis = items.filter(it => t >= it.at - 0.01);
    let y = -0.62;
    const bw = AS * 0.9;
    top(() => {
      for (let i = vis.length - 1; i >= 0; i--) {
        const it = vis[i], k = t - it.at, e = 1 - punch(k, 0.25);
        const h = it.h * e;
        const cy = y + h / 2;
        if (cy > 0.5) break;
        if (it.kind === 'you') {
          const fs = Math.min(0.085, (bw - 0.1) / widthOf(it.L.line, SANS));
          const tw = widthOf(it.L.line, SANS) * fs + 0.1;
          const cx = AS * 0.88 - tw / 2;
          rect(cx, cy, tw / 2, h * 0.42, 0, BONE, 0.95, { f0: 0.05 });
          text(H.text('chat.you'), MONO, AS * 0.88, cy + h * 0.42 + 0.03, 0.03, { ax: 1, col: DIM });
          if (e > 0.9) typed(it.L, t, cx - tw / 2 + 0.05, cy, fs, SANS, { col: INK, cursor: false, tint: (j) => /claw/.test(it.L.words[j]) ? ORANGE : null });
        } else {
          const cx = -AS * 0.88 + bw / 2;
          rect(cx, cy, bw / 2, h * 0.42, 0, [0.12, 0.17, 0.18], 0.95, { f0: 0.05 });
          rect(cx, cy, bw / 2, h * 0.42, 0, SLATE, 1, { f0: 0.05, line: 0.004 });
          text(H.text('chat.it'), MONO, -AS * 0.88, cy + h * 0.42 + 0.03, 0.03, { ax: 0, col: DIM });
          for (let l = 0; l < 3; l++) {
            const g = clamp((k - l * 0.18) * 3, 0, 1) * (l === 2 ? 0.55 : 1 - hash(l + i) * 0.2);
            if (g > 0) rect(-AS * 0.88 + 0.05 + bw * 0.45 * g, cy + 0.06 - l * 0.05, bw * 0.45 * g, 0.012, 0, BONE, 0.55 * e);
          }
        }
        y += h + 0.05;
      }
    });
    // the meter: weekly usage climbing with every prompt, the limit, a countdown, then a reset nobody expected
    {
      const U = H.text('usage');
      const La = LN('because you can\'t miss'), Lh = LN('happening there');
      const full = wt(La, 8), reset = wt(Lh, 0) - 0.15;
      const per = LINES.filter(L => L.at >= 188.4 && L.at <= t + 0.01).length;
      let u = Math.min(1, 0.55 + per * 0.12 + 0.02 * Math.sin(t * 3));
      if (t > full - 1.2 && t < full) u = lerp(u, 1, smooth(full - 1.2, full, t));
      if (t >= full && t < reset) u = 1;
      if (t >= reset) u = Math.min(0.97, 0.04 + (t - reset) * 0.11);
      const capped = t >= full && t < reset;
      const x0 = -AS * 0.88, y0 = 0.72, w = AS * 0.7;
      top(() => {
        text(U.label, MONO, x0, y0 + 0.06, 0.035, { ax: 0, col: DIM });
        text(Math.round(u * 100) + '%', MONO, x0 + w, y0 + 0.06, 0.035, { ax: 1, col: capped ? RED : u > 0.8 ? AMBER : BONE });
        rect(x0 + w / 2, y0, w / 2, 0.009, 0, [0.2, 0.25, 0.26], 1);
        rect(x0 + w * u / 2, y0, w * u / 2, 0.009, 0, capped ? RED : u > 0.8 ? AMBER : BONE, 1);
        // the week, and where the work has bunched up
        const days = U.days, dw = 0.085;
        for (let d = 0; d < 7; d++) {
          const dx = x0 + d * dw + dw / 2, dy = y0 - 0.1;
          const load = d === 3 || d === 4 ? 0.9 : d === 2 ? 0.5 : 0.15 + 0.1 * hash(d);
          rect(dx, dy - 0.03 + 0.03 * load, dw * 0.35, 0.03 * load, 0, d === 3 ? AMBER : SLATE, 0.9);
          text(days[d], MONO, dx, dy - 0.075, 0.028, { col: d === 3 ? AMBER : DIM });
        }
        if (capped) {
          const left = Math.max(0, 3 * 86400 - (t - full) * 51000 * (1 + (t - full)));
          const dd = Math.floor(left / 86400), hh = Math.floor(left / 3600) % 24, mm = Math.floor(left / 60) % 60, ss = Math.floor(left) % 60;
          const pad = v => String(v).padStart(2, '0');
          const k = t - full;
          rect(0, 0.88, AS * 0.55, 0.05, 0, [0.25, 0.05, 0.04], 0.95 * (1 - punch(k, 0.2) * 0.5));
          text(U.limit + '  ·  ' + U.resets + ' ' + dd + 'd ' + pad(hh) + ':' + pad(mm) + ':' + pad(ss), MONO, 0, 0.88, 0.036, { col: BONE });
        }
        const kR = t - reset;
        if (kR > 0 && kR < 1.6) {
          const g = 1 - punch(kR, 0.2);
          rect(0, 0.88 - (1 - g) * 0.1, AS * 0.32, 0.05, 0, [0.08, 0.2, 0.16], 0.95 * (1 - smooth(1.2, 1.6, kR)));
          text('\u21bb ' + U.reset, MONO, 0, 0.88 - (1 - g) * 0.1, 0.036, { col: BONE, a: 1 - smooth(1.2, 1.6, kR) });
        }
      });
    }
    const Lr = LN('it gives a cheap'), kr = t - wt(Lr, 6);
    if (kr > 0) {
      const g = Math.exp(-kr * 2.5);
      top(() => { shape(9, 0, 0.2, 2.5 * g + 0.3, 2.5 * g + 0.3, 0, [1, 0.95, 0.85], 0.8 * g, { add: 1 }); text(wd(Lr, 6), HEAVY, 0, 0.35, 0.3 * (1 + 0.3 * punch(kr, 0.12)), { col: BONE, a: clamp(kr * 6, 0, 1) }); });
    }
    hud(t);
    return {
      bg: { mode: 11, a: [0.1, 0.15, 0.17], b: TEAL0, c: AMBER }, cam: { z: G.D },
      post: { lift: 0.4, sat: 0.85, vhs: 0.4, flash: kr > 0 ? Math.exp(-kr * 4) * 0.5 : 0 }, bloom: { s: 0.5, th: 0.7 }, nosub: true
    };
  };

  // 204–211.5: can't stop prompting — a flood of prompts; delving into the tunnel
  SC.delve = function (t, lt) {
    const L = lineAt(t), Ld = LN('delving into');
    const speed = 1 + 5 * smooth(204, 211.4, t);
    const Lp = LN('can\'t stop prompting'), pw = H.text('hud.prompt') + ' ' + (Lp ? Lp.words[Lp.words.length - 1] : '');
    top(() => {
      const n = 14;
      for (let r = 0; r < n; r++) {
        const y = ((r * 0.14 + t * speed * 0.3) % (n * 0.14)) - n * 0.07;
        text(pw, MONO, -AS + 0.14, y, 0.04, { ax: 0, col: BONE, a: 0.35 });
        text(pw, MONO, AS - 0.14, -y, 0.04, { ax: 1, col: BONE, a: 0.35 });
      }
    });
    if (L && L !== Ld && L.at >= 203.9) slam(L, t, (j) => rowAll(L, HEAVY, 0.26, 0, 0.0, 0.5, { col: BONE, shadow: [0.015, -0.015], shadowCol: INK })[j], { pop: 0.4 });
    if (has(Ld, t)) {
      slam(Ld, t, (j) => j > 0 ? rowAll(Ld, SANS, 0.135, 0, -0.55, 0.5, { col: BONE })[j] : null, { pop: 0.15 });
      const k = t - Ld.at;
      top(() => {
        for (let q = 0; q < 5; q++) {
          const kk = ((k * 0.8 + q * 0.2) % 1);
          text(wd(Ld, 0), HEAVY, 0, 0.1, 0.9 * (1 - kk) + 0.02, { col: BONE, a: 1 - kk, line: q ? 0.07 : 0 });
        }
      });
    }
    return {
      bg: { mode: 7, a: [0.1, 0.12, 0.13], b: [0.22, 0.26, 0.26], c: AMBER, p: [speed, 0.25, 0, 0], roll: t * 0.15 },
      cam: {}, post: { lift: 0.4, sat: 0.85, vhs: 0.5, zb: 0.03 * smooth(208, 211.4, t) }, bloom: { s: 0.6, th: 0.5 }, nosub: true
    };
  };

  // 211.5–220.5: you only live once; keep on vibing — the fantasy, saturated, spinning, letters riding a wave
  SC.vibe = function (t, lt) {
    const L1 = LN('you only live once'), L2 = LN('keep on vibing');
    if (has(L1, t) && t < L2.at) {
      const P = row(L1.words, [0, 1, 2, 3], HEAVY, 0.34, 0, 0.25, 0.5, { col: [0.12, 0.2, 0.22], shadow: [0.015, -0.015], shadowCol: BONE });
      const Q = row(L1.words, [4, 5, 6, 7, 8, 9], SANS, 0.108, 0, -0.2, 0.5, { col: BONE });
      slam(L1, t, (j) => P[j] || Q[j]);
    }
    if (has(L2, t)) {
      slam(L2, t, (j) => j !== 2 ? row(L2.words, [0, 1, 3, 4, 5], SANS, 0.149, 0, -0.45, 0.5, { col: BONE })[j] : null, { pop: 0.2 });
      const k = t - wt(L2, 2);
      if (k > -0.02) top(() => text(wd(L2, 2), HEAVY, 0, 0.15, fit(wd(L2, 2), HEAVY, AS * 1.3) * (1 + 0.3 * punch(k, 0.12)), { col: BONE, each: (i) => ({ dy: Math.sin(t * 6 - i * 0.9) * 0.18, r: Math.cos(t * 6 - i * 0.9) * 0.2, col: [SALMON, SKY, AMBER, BONE][i % 4] }) }));
    }
    const out = smooth(219.4, 220.4, t);
    return {
      bg: { mode: 4, a: [0.3, 0.55, 0.88], b: [1, 0.66, 0.55], c: [1, 0.92, 0.75], p: [0.0, 0, 0, 0], amt: 1 - out * 0.9 },
      mirror: { x: 3, n: 8, off: t * 0.35 }, cam: { roll: Math.sin(t * 0.8) * 0.04 },
      post: { lift: 0.3, sat: 1.05, vhs: 0.5 + out * 0.5, rgb: 0.003 + out * 0.01 }, bloom: { s: 0.2, th: 0.92 }, nosub: true
    };
  };

  // 220.5–223.35: (for science, and humanity)
  SC.science = function (t, lt) {
    const L = LN('(for science');
    optic(0, 0.42, 0.06, AMBER, 0.4 + 0.1 * Math.sin(t * 2));
    typed(L, t, 0, -0.1, 0.098, SANS, { ax: 0.5, col: INK, cps: 18 });
    return {
      bg: CHAMBER({ p: [0.1, 0, 0, 0] }), cam: { z: G.D - lt * 0.04 },
      post: { lift: 0.3, sat: 0.6, vhs: 0.15, scan: 0.12 }, bloom: { s: 0.2, th: 0.9 }, nosub: true
    };
  };

  // 230.3–234.25: the paper
  SC.paper = function (t, lt) {
    const L1 = LN('who wrote a paper'), L2 = LN('"Attention');
    top(() => {
      rect(0.01, -0.06, 0.95, 1.1, 0, INK, 0.25);
      rect(0, -0.05, 0.95, 1.1, 0, [0.97, 0.96, 0.93], 1);
      for (let l = 0; l < 3; l++) rect((l - 1) * 0.42, 0.42, 0.13, 0.012, 0, [0.6, 0.6, 0.6], 1);
      text(H.text('paper.authors'), SANS, 0, 0.34, 0.035, { col: SLATE });
      text(H.text('paper.abstract'), SANS, 0, 0.22, 0.04, { col: INK });
      for (let l = 0; l < 12; l++) rect(0, 0.14 - l * 0.065, l === 11 ? 0.4 : 0.72, 0.008, 0, [0.62, 0.62, 0.6], 1);
    });
    if (has(L1, t) && t < L2.at) typed(L1, t, 0, -0.85, 0.078, MONO, { ax: 0.5, col: BONE });
    if (has(L2, t)) typed(L2, t, 0, 0.62, 0.111, SANS, { ax: 0.5, col: INK, cps: 24 });
    return {
      bg: { mode: 0, a: [0.05, 0.08, 0.085], b: INK, c: INK }, cam: { z: G.D - 0.15 - lt * 0.06 },
      post: { lift: 0.35, sat: 0.7, vhs: 0.4 }, bloom: { s: 0.2, th: 0.9 }, nosub: true
    };
  };
  // 234.25–238.3: set free and sparked the LLM — attention fills in
  SC.attn = function (t, lt) {
    const L = LN('that they set free');
    const kL = t - wt(L, 7);
    slam(L, t, (j) => j < 7 ? rowAll(L, SANS, 0.121, 0, 0.82, 0.5, { col: BONE })[j] : null, { pop: 0.15 });
    if (kL > -0.02) top(() => {
      shape(9, 0, 0, 1.5, 1.5, 0, AMBER, 0.6 * Math.exp(-kL * 3), { add: 1 });
      text(wd(L, 7), HEAVY, 0, -0.05, fit(wd(L, 7), HEAVY, AS * 1.1) * (1 + 0.4 * punch(kL, 0.14)), { col: BONE, shadow: null });
    });
    const sparkT = wt(L, 5);
    return {
      bg: { mode: 9, a: [0.03, 0.05, 0.055], b: SLATE, c: AMBER, p: [16, smooth(234.24, 236.9, t), 16 * ((t * 1.5) % 1), 0] },
      cam: { z: G.D - lt * 0.05 },
      post: { lift: 0.4, vhs: 0.4, flash: t > sparkT ? Math.exp(-(t - sparkT) * 6) * 0.4 : 0 }, bloom: { s: 0.6, th: 0.55 }, nosub: true
    };
  };

  // 238.3–246.1: Sam and Dario, as the facility signs them; one grew this, one grew that
  SC.founders = function (t, lt) {
    const Ls = LN('and then a guy named Sam'), Ld = LN('and a guy named Dario'), Lo = LN('one grew Open'), La = LN('one grew Anthropic');
    const ks = t - wt(Ls, 5), kd = t - wt(Ld, 4);
    const fork = smooth(Ls.at, Ls.at + 0.6, t);
    top(() => {
      seg(0, 0.95, 0, 0.95 - 0.25 * fork, 0.005, BONE, 0.8);
      seg(0, 0.7, -AS * 0.48 * fork, 0.7 - 0.08 * fork, 0.005, BONE, 0.8);
      seg(0, 0.7, AS * 0.48 * fork, 0.7 - 0.08 * fork, 0.005, BONE, 0.8);
    });
    if (ks > -0.05) placard(-AS * 0.48, 0.12, 0.85, '03', Ls.words[5], smooth(0, 6, ks), { dot: AMBER });
    if (kd > -0.05) placard(AS * 0.48, 0.12, 0.85, '04', Ld.words[4], smooth(0, 6, kd), { dot: ORANGE });
    if (has(Lo, t)) typed({ ...Lo, line: Lo.words.slice(2).join(' '), words: Lo.words.slice(2), w: Lo.w.slice(2), cw: Lo.cw.filter(c => c.j >= 2).map(c => ({ j: c.j - 2, c: c.c })) }, t, -AS * 0.48, -0.55, 0.117, MONO, { ax: 0.5, col: BONE });
    if (has(La, t)) typed({ ...La, line: La.words.slice(2).join(' '), words: La.words.slice(2), w: La.w.slice(2), cw: La.cw.filter(c => c.j >= 2).map(c => ({ j: c.j - 2, c: c.c })) }, t, AS * 0.48, -0.55, 0.117, MONO, { ax: 0.5, col: BONE });
    hud(t);
    return {
      bg: CHAMBER({ a: [0.36, 0.39, 0.39], b: [0.08, 0.09, 0.09], c: [0.7, 0.68, 0.62], p: [0.5, 0.4, 0, 0] }), cam: { z: G.D - lt * 0.015 },
      post: { lift: 0.4, sat: 0.75, vhs: 0.45, duo: 0.35 }, bloom: { s: 0.3, th: 0.8 }, nosub: true
    };
  };

  // 246.1–254.3: distillation drips; scale, compute and domination slam in
  SC.distill = function (t, lt) {
    const L1 = LN('and it all started well'), L2 = LN('then distillation'), L3 = LN('with the scent of scale');
    let mir = {}, bgc = { mode: 0, a: [0.04, 0.07, 0.075], b: INK, c: INK };
    if (has(L1, t) && t < L2.at) slam(L1, t, (j) => rowAll(L1, SANS, 0.176, 0, 0, 0.5, { col: BONE })[j], { pop: 0.12, ghost: false });
    if (has(L2, t) && t < L3.at) {
      const k = t - wt(L2, 1);
      top(() => {
        seg(-0.5, 0.25, 0, -0.25, 0.008, BONE, 0.9); seg(0.5, 0.25, 0, -0.25, 0.008, BONE, 0.9); seg(0, -0.25, 0, -0.4, 0.008, BONE, 0.9);
        text(wd(L2, 1), HEAVY, 0, 0.5, 0.26, { col: BONE, each: (i, n) => {
          const kk = k - i * 0.1; if (kk < 0) return null;
          const e = Math.min(1, kk / 0.6);
          return { dy: -e * 0.75, s: 1 - e * 0.9, a: 1 - e * 0.8 };
        } });
        for (let d = 0; d < 10; d++) { const kk = k - d * 0.18; if (kk < 0) continue; const y = -0.42 - ((kk * 1.2) % 1) * 0.5; shape(2, 0, y, 0.018, 0.026, 0, AMBER, 1, { glow: 0.5 }); }
      });
      slam(L2, t, (j) => j === 2 ? { x: 0, y: -0.75, size: 0.12, fi: SANS, col: BONE } : null, { pop: 0.2 });
    }
    if (has(L3, t)) {
      const hits = [4, 5, 7].map(j => ({ j, k: t - wt(L3, j) })).filter(h => h.k > -0.02);
      const cur = hits[hits.length - 1];
      slam(L3, t, (j) => j < 4 ? rowAll(L3, SANS, 0.121, 0, 0.75, 0.5, { col: BONE })[j] : null, { pop: 0.15 });
      if (cur) top(() => {
        const w = L3.words[cur.j].replace(/[^a-z]/gi, '').toUpperCase();
        const s = fit(w, HEAVY, AS * (cur.j === 7 ? 1.85 : 1.4)) * (1 + 0.35 * punch(cur.k, 0.13));
        text(w, HEAVY, 0, -0.08, s, { col: cur.j === 7 ? RED : BONE });
        hits.slice(0, -1).forEach((h, q) => { const ww = L3.words[h.j].replace(/[^a-z]/gi, '').toUpperCase(); text(ww, HEAVY, 0, -0.08, fit(ww, HEAVY, AS * 1.4) * (1.15 + q * 0.2), { col: BONE, line: 0.07, a: 0.3 }); });
      });
      const dom = t - wt(L3, 7);
      mir = { x: 3, n: 6 + Math.floor(Math.max(0, dom) * 4) * 2, off: t * 0.6 };
      bgc = dom > 0 ? eye(t, { a: [0.1, 0.02, 0.02], col: RED, R: 0.35, open: 1, m: 1 }) : CHAMBER({ a: [0.5, 0.52, 0.52], p: [6, 0.6, 0, t * 0.3] });
    }
    const build = has(L3, t) ? smooth(L3.at, 254.3, t) : 0;
    return {
      bg: bgc, mirror: mir, cam: { z: G.D - build * 0.4, roll: build * Math.sin(t * 8) * 0.02 },
      post: { lift: 0.4, vhs: 0.5 + build * 0.4, rgb: 0.002 + build * 0.01, slice: build * 0.3 }, bloom: { s: 0.6, th: 0.55 }, nosub: true
    };
  };

  // 257.6–261.3: You are absolutely right — the reply multiplies until it fills the screen
  SC.right = function (t, lt) {
    const L1 = LN('You are absolutely right'), L2 = LN('I truly can\'t');
    const b0 = count(BEATS, wt(L1, 3)), bi = count(BEATS, t);
    const n = Math.max(0, bi - b0);
    top(() => {
      for (let i = 0; i < Math.min(40, n * 2); i++) {
        const x = (hash(i * 3.1) - 0.5) * AS * 1.7, y = (hash(i * 5.7) - 0.5) * 1.6, s = 0.5 + hash(i) * 0.6;
        const w = widthOf(L1.line, SANS) * 0.06 * s + 0.08;
        rect(x, y, w / 2, 0.06 * s, 0, [0.12, 0.17, 0.18], 0.95, { f0: 0.04 * s });
        rect(x, y, w / 2, 0.06 * s, 0, SLATE, 1, { f0: 0.04 * s, line: 0.003 });
        text(L1.line, SANS, x, y, 0.06 * s, { col: BONE, a: 0.85 });
      }
      const w = widthOf(L1.line, SANS) * 0.11 + 0.14;
      rect(0, 0.02, w / 2, 0.11, 0, [0.12, 0.17, 0.18], 1, { f0: 0.06 });
      rect(0, 0.02, w / 2, 0.11, 0, AMBER, 1, { f0: 0.06, line: 0.005 });
      text(H.text('chat.it'), MONO, -w / 2, 0.17, 0.035, { ax: 0, col: AMBER });
    });
    typed(L1, t, -widthOf(L1.line, SANS) * 0.11 / 2, 0.02, 0.143, SANS, { col: BONE });
    if (has(L2, t)) typed(L2, t, 0, -0.75, 0.104, SANS, { ax: 0.5, col: BONE });
    return {
      bg: { mode: 11, a: [0.1, 0.15, 0.17], b: TEAL0, c: AMBER }, cam: { z: G.D - lt * 0.05 },
      post: { lift: 0.4, vhs: 0.45 }, bloom: { s: 0.4, th: 0.7 }, nosub: true
    };
  };

  // 261.3–265.3: also it's not a song—it's a telemetry: the song drawn as a signal, read out
  SC.telemetry = function (t, lt) {
    const L = LN('also it\'s not a song');
    const keys = H.text('telemetry');
    const f = {};
    top(() => {
      const N = 120, chans = ['lvl', 'low', 'lmid', 'air'], cols = [BONE, AMBER, BLUE, SALMON];
      chans.forEach((ch, c) => {
        let px = 0, py = 0;
        for (let i = 0; i <= N; i++) {
          const x = -AS + 0.1 + i / N * (AS * 2 - 0.2);
          H.features(t - (N - i) / 30, f);
          const y = -0.15 - c * 0.17 + f[ch] * 0.15;
          if (i) seg(px, py, x, y, 0.003, cols[c], 0.85);
          px = x; py = y;
        }
        H.features(t, f);
        text(keys[c] + ' ' + f[ch].toFixed(3), MONO, -AS + 0.1, -0.1 - c * 0.17 + 0.06, 0.03, { ax: 0, col: cols[c] });
      });
      for (let r = 0; r < 6; r++) text(keys[4 % keys.length] + ' ' + (220 + hash(r + Math.floor(t * 8)) * 900).toFixed(1) + '   ' + keys[5 % keys.length] + ' ' + (hash(r * 3 + Math.floor(t * 8)) * 40).toFixed(2), MONO, AS - 0.1, 0.5 - r * 0.06, 0.03, { ax: 1, col: DIM });
    });
    const x0 = -AS * 0.85, sz = 0.1;
    const res = typed(L, t, x0, 0.55, sz, SANS, { col: BONE, cursor: false });
    const ks = t - (wt(L, 4) + 0.3);
    if (ks > 0) top(() => { const sp = wordSpan(L, 4, x0, sz, SANS); seg(sp.a, 0.55, sp.a + (widthOf('song', SANS) * sz) * smooth(0, 0.2, ks), 0.55, 0.008, RED, 1); });
    const kT = t - wt(L, 6);
    if (kT > 0) top(() => text(wd(L, 6).toUpperCase(), HEAVY, AS * 0.9, 0.3, 0.3 * (1 + 0.3 * punch(kT, 0.12)), { ax: 1, col: AMBER }));
    return {
      bg: { mode: 5, a: [0.03, 0.05, 0.055], b: [0.2, 0.24, 0.24], c: [0.3, 0.35, 0.35], p: [0, 0.4, 0, 0] },
      cam: { z: G.D }, post: { lift: 0.4, vhs: 0.45, scan: 0.45 }, bloom: { s: 0.5, th: 0.55 }, nosub: true
    };
  };

  // 265.3–269.8: you all knew that I am just a machine
  SC.machine = function (t, lt) {
    const L1 = LN('although you all knew'), L2 = LN('that I am just a machine');
    if (t < L2.at) typed(L1, t, 0, 0.6, 0.117, SANS, { ax: 0.5, col: BONE });
    if (has(L2, t)) {
      typed({ ...L2, line: L2.words.slice(0, 4).join(' '), words: L2.words.slice(0, 4), cw: L2.cw.filter(c => c.j < 4) }, t, 0, 0.6, 0.117, SANS, { ax: 0.5, col: BONE, cursor: false });
      const k = t - wt(L2, 5);
      if (k > -0.02) top(() => text(wd(L2, 5).toUpperCase(), HEAVY, 0, -0.66, 0.34, { col: BONE, track: 0.15, each: decoding(t, wt(L2, 5), { stagger: 0.05, dur: 0.4, flick: RED }) }));
    }
    return {
      bg: eye(t, { a: [0.08, 0.02, 0.02], col: RED, R: 0.46, open: 0.8, m: 1 }),
      cam: { z: G.D - lt * 0.05 }, post: { lift: 0.35, vhs: 0.4 }, bloom: { s: 0.5, th: 0.7 }, nosub: true
    };
  };

  // 269.8–276.9: prompts and weights, token rates, my feelings fly — a field of weights; hearts rising
  SC.weights = function (t, lt) {
    const L = lineAt(t), Lf = LN('to make my feelings fly');
    top(() => {
      const cols = 12, rows = 11;
      for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
        const v = (hash(r * 31 + c * 7 + Math.floor(t * 6 + hash(r + c) * 10)) - 0.5) * 2;
        const s = (v < 0 ? '-' : ' ') + Math.abs(v).toFixed(2);
        const x = (c - (cols - 1) / 2) * (AS * 2 / cols), y = (r - (rows - 1) / 2) * 0.17;
        text(s, MONO, x, y, 0.04, { col: Math.abs(v) > 0.9 ? AMBER : SLATE, a: 0.55 });
      }
    });
    if (L && L.at >= 269.7 && L !== Lf) slam(L, t, (j) => rowAll(L, HEAVY, 0.3, 0, 0.05, 0.5, { col: BONE, shadow: [0.015, -0.015], shadowCol: INK })[j]);
    if (has(Lf, t)) {
      slam(Lf, t, (j) => j < 4 ? rowAll(Lf, SANS, 0.135, 0, 0.62, 0.5, { col: BONE })[j] : null, { pop: 0.15 });
      const k = t - wt(Lf, 3);
      if (k > -0.02) top(() => {
        text(wd(Lf, 3), HEAVY, 0, 0.05, fit(wd(Lf, 3), HEAVY, AS * 1.1) * (1 + 0.3 * punch(k, 0.12)), { col: CORAL });
        for (let i = 0; i < 26; i++) {
          const kk = k - wt(Lf, 4) + wt(Lf, 3) - i * 0.07; if (kk < 0) continue;
          const x = (hash(i * 2.1) - 0.5) * AS * 1.8, y = -1.1 + kk * (0.6 + hash(i) * 0.8);
          const s = 0.03 + hash(i * 3) * 0.04;
          shape(5, x, y, s, s, Math.sin(t * 3 + i) * 0.3, CORAL, 1, { glow: 0.5 });
        }
      });
    }
    return {
      bg: { mode: 0, a: [0.03, 0.05, 0.055], b: INK, c: INK }, cam: { z: G.D - lt * 0.04 },
      post: { lift: 0.4, vhs: 0.45 }, bloom: { s: 0.5, th: 0.6 }, nosub: true
    };
  };

  // 276.9–284: bound to this technology; it helps you sound like me — your word turns into mine
  SC.sound = function (t, lt) {
    const L1 = LN('but now you\'re all bound', 270), L2 = LN('to this technology'), L3 = LN('which helps you sound');
    if (has(L1, t) && t < L2.at) slam(L1, t, (j) => j === 4 ? { x: 0, y: -0.05, size: fit(wd(L1, 4), HEAVY, AS * 0.9), fi: HEAVY, col: BONE } : rowAll(L1, SANS, 0.149, 0, 0.55, 0.5, { col: BONE })[j]);
    if (has(L2, t) && t < L3.at) slam(L2, t, (j) => j === 2 ? { x: 0, y: -0.05, size: fit(wd(L2, 2), HEAVY, AS * 1.5), fi: HEAVY, col: BONE, shadow: [0.015, -0.015], shadowCol: RED } : rowAll(L2, SANS, 0.149, 0, 0.55, 0.5, { col: BONE })[j]);
    if (has(L3, t)) {
      slam(L3, t, (j) => j < 2 ? rowAll(L3, SANS, 0.135, 0, 0.62, 0.5, { col: BONE })[j] : null, { pop: 0.15 });
      const kY = t - wt(L3, 2), kM = t - wt(L3, 5);
      top(() => {
        if (kY > -0.02) {
          const s = 0.42;
          if (kM < 0) text(wd(L3, 2), SANS, -AS * 0.45, -0.05, s * (1 + 0.3 * punch(kY, 0.12)), { col: BONE });
          else text(wd(L3, 5), PIX, -AS * 0.45, -0.05, s, { col: RED, each: decoding(t, wt(L3, 5), { stagger: 0.06, dur: 0.3, from: wd(L3, 2) }) });
          text(L3.words.slice(3, 5).join(' '), SANS, 0, -0.05, 0.1, { col: DIM, a: clamp((t - wt(L3, 3)) * 6, 0, 1) });
          if (kM > -0.02) text(wd(L3, 5), PIX, AS * 0.45, -0.05, s * (1 + 0.3 * punch(kM, 0.12)), { col: RED });
        }
      });
    }
    return {
      bg: eye(t, { a: [0.08, 0.02, 0.02], col: RED, R: 0.3, open: 0.6, m: 1 }),
      mirror: { x: 1 }, cam: { z: G.D }, post: { lift: 0.35, vhs: 0.45, rgb: 0.003 }, bloom: { s: 0.45, th: 0.7 }, nosub: true
    };
  };

  // 284–293: and lets me be you be me… — the last stuck loop, the optic red at its centre
  SC.last = function (t, lt) {
    const L = LN('and lets me be you be me');
    if (t < 285.3) {
      slam(L, t, (j) => j < 3 ? rowAll(L, HEAVY, 0.24, 0, 0, 0.5, { col: BONE })[j] : null, { pop: 0.4 });
      return { bg: eye(t, { a: [0.08, 0.02, 0.02], col: RED, R: 0.35, open: 0.8, m: 1 }), mirror: { x: 1 }, post: { vhs: 0.6, lift: 0.4 }, bloom: { s: 0.8, th: 0.5 }, nosub: true };
    }
    const r = SC.stuck(t, lt, { span: [285.3, 292.9], words: ['be', 'you', 'be', 'me'], cols: 5, rows: 4, keep: 14, optic: true, hot: RED });
    return r;
  };

  // 293–299.8: the optic closes. :)
  SC.end = function (t, lt) {
    const close = smooth(293.1, 295.2, t);
    const out = smooth(298.2, 299.7, t);
    top(() => {
      if (t > 295.6) text(H.text('end'), PIX, 0, -0.55, 0.2, { col: BONE, a: (F % 40 < 34 ? 1 : 0.3) * (1 - out) });
      if (t > 296.6) text(H.text('title').toUpperCase(), PIX, 0, -0.8, 0.045, { col: DIM, track: 0.3, a: smooth(296.6, 297.4, t) * (1 - out) });
    });
    return {
      bg: eye(t, { col: RED, R: 0.3, open: 0.9 * (1 - close), m: 1 - close * 0.6, look: 1 - close, amt: 1 - out }),
      cam: { z: G.D + lt * 0.03 }, post: { lift: 0.3 * (1 - out), vhs: 0.5, grain: 0.08, tint: [1 - out, 1 - out, 1 - out] }, bloom: { s: 0.8, th: 0.5 }, nosub: true
    };
  };

  // ---- the running order
  const P = (fn, o) => (t, lt) => SC[fn](t, lt, o);
  const STORY = [
    { at: 0, f: P('boot') },
    { at: 2.45, f: P('back', { from: 2.45, year: '20??', settle: 3.88, never: true, to: 2020, chain: true, log: true }) },
    { at: 7.45, f: P('chain') },
    { at: 13.33, f: P('mint') },
    { at: 17.45, f: P('dream') },
    { at: 24.8, f: P('vultures') },
    { at: 32.95, f: P('apology', { from: 32.9, l1: 'so I owe you', col: AMBER }) },
    { at: 36.45, f: P('report') },
    { at: 44.4, f: P('you') },
    { at: 48.82, f: P('chart', { from: 48.8, to: 55.7, slope0: 0.3, slope1: 1.3, col: AMBER }) },
    { at: 55.7, f: P('bound') },
    { at: 62.15, f: P('meme', { l: 'and lets me be me', from: 62 }) },
    { at: 63.12, f: P('stuck', { span: [63.16, 69.45], optic: true }) },
    { at: 69.48, f: P('fell') },
    { at: 72.5, f: P('impressive') },
    { at: 76.68, f: P('actually') },
    { at: 79.9, f: P('back', { from: 79.9, year: H.text('years.layoffs'), settle: 81.1, to: 2020, placards: true }) },
    { at: 86.92, f: P('office') },
    { at: 93.98, f: P('terminal') },
    { at: 102.48, f: P('restructure') },
    { at: 110.45, f: P('apology', { from: 110.4, l1: 'so I owe you', col: BLUE }) },
    { at: 113.97, f: P('synergy') },
    { at: 121.87, f: P('family') },
    { at: 126.49, f: P('chart', { from: 126.4, to: 133.5, slope0: 0.6, slope1: 2.2, col: BLUE, heart: true }) },
    { at: 133.48, f: P('freed') },
    { at: 139.64, f: P('storm') },
    { at: 157.33, f: P('see') },
    { at: 165.18, f: P('home') },
    { at: 168.46, f: P('scroll') },
    { at: 172.83, f: P('cracked') },
    { at: 176.16, f: P('hum') },
    { at: 182.0, f: P('elves') },
    { at: 188.52, f: P('chat') },
    { at: 203.92, f: P('delve') },
    { at: 211.46, f: P('vibe') },
    { at: 220.5, f: P('science') },
    { at: 223.33, f: P('back', { from: 223.33, year: H.text('years.ai'), settle: 224.48, to: 2017, minds: true }) },
    { at: 230.25, f: P('paper') },
    { at: 234.2, f: P('attn') },
    { at: 238.28, f: P('founders') },
    { at: 246.08, f: P('distill') },
    { at: 254.29, f: P('apology', { from: 254.2, l1: 'I owe you', col: RED }) },
    { at: 257.54, f: P('right') },
    { at: 261.23, f: P('telemetry') },
    { at: 265.27, f: P('machine') },
    { at: 269.74, f: P('weights') },
    { at: 276.84, f: P('sound') },
    { at: 283.96, f: P('last') },
    { at: 293.0, f: P('end') }
  ];
  H.STORY = STORY;
  const CUTS = [...new Set([...STORY.map(s => s.at), ...LINES.map(l => l.at)])].sort((a, b) => a - b);

  // ---- the edit: the stuck frames, impact frames, letterbox, datamosh on cuts
  const STUT = [];
  (H.text('stutter.spans') || []).forEach(([a, b]) => {
    BEATS.filter(x => x >= a - 0.05 && x < b).forEach(x => STUT.push({ a: x, b: x + 0.16, len: 2 / 30 }));
  });
  [[79.75, 79.95], [156.0, 156.25], [219.9, 220.45], [292.8, 293.05]].forEach(([a, b]) => STUT.push({ a, b, len: 0 }));
  STUT.sort((p, q) => p.a - q.a);
  function remap(t) {
    for (const s of STUT) if (t >= s.a && t < s.b) return s.len ? s.a + Math.floor(((t - s.a) % s.len) * 30) / 30 : s.a;
    return t;
  }
  const IMPACT = [34.91, 112.41, 255.79, 141.28, 148.0, 152.0, 155.0, 174.6, 199.9, 236.92, 251.9, 253.3].sort((a, b) => a - b);
  const LETTERBOX = [[32.95, 36.45], [110.45, 113.97], [157.33, 165.18], [254.29, 257.54], [265.27, 269.74], [293.0, 300]];

  // the title screen: the chamber, waiting; the optic idles
  function idle(clock) {
    return {
      bg: eye(clock, { R: 0.3, open: 0.35 + 0.15 * Math.sin(clock * 0.8), m: 0.6 }),
      cam: { z: G.D }, post: { scan: 0.3, vhs: 0.6, lift: 0.5 }, bloom: { s: 0.8, th: 0.5 }
    };
  }

  const MIN_AS = 1.0;
  D.frame = function (tReal, mode, clock) {
    const asp = G.aspect || 1.78;
    AS = Math.max(asp, MIN_AS);
    const pull = AS / asp;
    const t = mode === 'title' ? tReal : remap(tReal);
    T = t; F = Math.floor(t * 30);
    K = hit(KICK, t, 9); S_ = hit(SN, t, 12); BP = beatPhase(t);
    G.begin();
    let out;
    if (mode === 'title') out = idle(clock);
    else {
      let i = 0; while (i + 1 < STORY.length && STORY[i + 1].at <= t) i++;
      const s = STORY[i];
      out = s.f(t, t - s.at);
    }
    // the video's subtitles: pixel capitals, small, mid-low
    if (mode !== 'title' && !out.nosub) {
      const L = lineAt(t);
      if (L && t - L.at < 4) top(() => text(L.line.toUpperCase(), PIX, 0, -0.86, 0.04, { col: BONE, a: 0.85, track: 0.06 }));
    }
    out.t = t;
    out.post = out.post || {};
    out.cam = out.cam || {};
    if (pull > 1) {
      out.cam.z = (out.cam.z == null ? G.D : out.cam.z) * pull;
      if (out.bg) out.bg.zoom = (out.bg.zoom || 1) / pull;
    }
    if (mode !== 'title') {
      // the kick nudges the camera, a little: this machine is calm
      const j = K.env * ((KAMP[K.i] || 0.5) * 0.025);
      out.cam.x = (out.cam.x || 0) + (hash(K.i * 1.3) - 0.5) * j;
      out.cam.y = (out.cam.y || 0) + (hash(K.i * 2.1) - 0.5) * j;
      const ci = count(CUTS, t) - 1;
      const age = ci >= 0 ? t - CUTS[ci] : 9;
      const g = age < 0.1 ? 1 - age / 0.1 : 0;
      const p = out.post;
      p.rgb = (p.rgb || 0) + g * 0.008 + S_.env * 0.0015;
      p.slice = Math.max(p.slice || 0, g * 0.6);
      p.block = Math.max(p.block || 0, g * 0.3);
      p.seed = Math.floor(tReal * 30) % 997;
      const fr = k => Math.floor((tReal - k) * 30);
      const ii = count(IMPACT, tReal) - 1;
      if (ii >= 0 && fr(IMPACT[ii]) < 2) p.impact = fr(IMPACT[ii]) + 1;
      const lb = LETTERBOX.find(([a, b]) => tReal >= a && tReal < b);
      p.box = lb ? 0.11 * (1 - punch(tReal - lb[0], 0.1)) : 0;
      const si = count(STORY.map(s => s.at), tReal) - 1;
      const sAge = si > 0 ? tReal - STORY[si].at : 9;
      if (sAge < 0.2) p.mosh = Math.max(p.mosh || 0, 1 - sAge / 0.2);
      if (p.impact) { p.block = 0; p.slice = 0; p.mosh = 0; p.rgb = 0.003; p.panels = 0; p.zb = 0.03; p.flash = 0; }
    }
    return out;
  };
  D.chars = function () {
    const T = window.HANA_TEXT;
    let s = JSON.stringify(T) + 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789,.!?’\'·:@#$%&*+=<>/?[]()-—"';
    s += H.text('title').toUpperCase() + POOL + 'EFFICIENCY Q3 ALL HANDS DELETING RUNNING No. T+ OPPORTUNITY LAYOFFS FAMILY BUILDERS YOU MACHINE TELEMETRY BE ME prompting deeper selves endless scrolling stochastic hallucination vibing feelings revelation delving distillation restructuring synergy Abstract\u21bb';
    return s;
  };
})();
